import rdfFactory, { createNS, Feature, QuadPosition, TermType, isLiteral, isNode, isQuad } from '@ontologies/core';
import { ns, type, first, rest, nil, Seq, Property, subject, Statement, predicate, object, List, value as value$1, Alt, Bag } from '@ontologies/rdf';
import { target, url, httpMethod, Thing } from '@ontologies/schema';
import { Resource, member as member$1, domain, range, subPropertyOf, subClassOf, Class, seeAlso, isDefinedBy, comment, label, Literal, Container, ContainerMembershipProperty, Datatype } from '@ontologies/rdfs';
import { ns as ns$1, add, purge, remove, replace, slice } from '@ontologies/ld';
import { integer } from '@ontologies/xsd';
import { site } from '@rdfdev/iri';
import { BAD_REQUEST, INTERNAL_SERVER_ERROR, NOT_FOUND } from 'http-status-codes';
import { sameAs } from '@ontologies/owl';
import { hextuplesTransformer } from 'hextuples';
import { NQuadsParser } from 'n-quads-parser';

const ll = createNS("http://purl.org/link-lib/");
var ll$1 = {
    ns: ll,
    defaultTopology: ll("defaultTopology"),
    graph: ll("graph"),
    meta: ll("meta"),
    nop: ll("nop"),
    targetResource: ll("targetResource"),
    typeRenderClass: ll("typeRenderClass"),
    ClientError: ll("ClientError"),
    ErrorResource: ll("ErrorResource"),
    ServerError: ll("ServerError"),
    /** @deprecated */
    add: ll("add"),
    /** @deprecated */
    purge: ll("purge"),
    /** @deprecated */
    remove: ll("remove"),
    /** @deprecated */
    replace: ll("replace"),
    /** @deprecated */
    slice: ll("slice"),
};

const F_NTRIPLES = "application/n-triples";
const F_TURTLE = "text/turtle";
const F_N3 = "text/n3";
const F_PLAIN = "text/plain";
const F_JSONLD = "application/ld+json";
const F_RDF_XML = "application/rdf+xml";
const F_NTRIPLES_DEP = "text/ntriples";
const F_TURTLE_DEP = "application/x-turtle";
const NON_CONTENT_EXTS = ["php", "asp", "aspx", "cgi", "jsp"];
const DEFAULT_TOPOLOGY = ll$1.defaultTopology;
/** Constant used to determine that a class is used to render a type rather than a property. */
const RENDER_CLASS_NAME = ll$1.typeRenderClass;
const MAIN_NODE_DEFAULT_IRI = ll$1.targetResource;
// tslint:disable-next-line ban-types
const NON_DATA_OBJECTS_CTORS = [
    Array,
    ArrayBuffer,
    Boolean,
    DataView,
    Date,
    Error,
    EvalError,
    Float32Array,
    Float64Array,
    Int16Array,
    Int32Array,
    Int8Array,
    Intl.Collator,
    Intl.DateTimeFormat,
    Intl.NumberFormat,
    Map,
    Number,
    Promise,
    /* istanbul ignore next */
    (typeof Proxy !== "undefined" ? Proxy : undefined),
    RangeError,
    ReferenceError,
    RegExp,
    Set,
].filter(Boolean);
const MSG_BAD_REQUEST = "Request failed with bad status code";
const MSG_INCORRECT_TARGET = "Collections or Literals can't be the target";
const MSG_URL_UNDEFINED = "No url given with action.";
const MSG_URL_UNRESOLVABLE = "Can't execute action with non-named-node url.";

class ComponentCache {
    constructor() {
        this.lookupCache = {};
    }
    /**
     * Adds a renderer to {this.lookupCache}
     * @param component The render component.
     * @param key The memoization key.
     * @returns The renderer passed with {component}
     */
    add(component, key) {
        this.lookupCache[key] = component;
        return this.lookupCache[key];
    }
    clear() {
        this.lookupCache = {};
    }
    /**
     * Resolves a renderer from the {lookupCache}.
     * @param key The key to look up.
     * @returns If saved the render component, otherwise undefined.
     */
    get(key) {
        return this.lookupCache[key];
    }
}

const MSG_TYPE_ERR = "Non-optimized NamedNode instance given. Please memoize your namespace correctly.";
/** Constant used to determine that a component is used to render a type rather than a property. */
const RENDER_CLASS_NAME$1 = ll$1.typeRenderClass;
function convertToCacheKey(types, props, topology) {
    return `${types.join()}[${props.join()}][${topology}]`;
}
const assert = (obj) => {
    if (obj === undefined) {
        throw new TypeError(MSG_TYPE_ERR);
    }
};
/**
 * Handles registration and querying for view components.
 */
class ComponentStore {
    constructor(schema) {
        this.lookupCache = new ComponentCache();
        /**
         * Lookup map ordered with the following hierarchy;
         * [propertyType][resourceType][topology]
         */
        this.registrations = {};
        this.schema = schema;
        this.registrations[RENDER_CLASS_NAME$1.value] = {};
    }
    /**
     * Generate registration description objects for later registration use.
     * @see LinkedRenderStore#registerAll
     */
    static registerRenderer(component, types, fields, topologies) {
        if (typeof component === "undefined") {
            throw new Error(`Undefined component was given for (${types}, ${fields}, ${topologies}).`);
        }
        const registrations = [];
        for (let t = 0; t < types.length; t++) {
            assert(types[t]);
            for (let p = 0; p < fields.length; p++) {
                assert(fields[p]);
                for (let top = 0; top < topologies.length; top++) {
                    assert(topologies[top]);
                    registrations.push({
                        component,
                        property: fields[p],
                        topology: topologies[top],
                        type: types[t],
                    });
                }
            }
        }
        return registrations;
    }
    /**
     * TODO: remove defaultType - Basically a bug. We default the type if no matches were found, rather than using
     *   inheritance to associate unknown types the RDF way (using rdfs:Resource).
     */
    getRenderComponent(types, fields, topology, defaultType) {
        const oTypes = this.schema.expand(types);
        const key = convertToCacheKey(oTypes, fields, topology);
        const cached = this.lookupCache.get(key);
        if (cached !== undefined) {
            return cached;
        }
        for (let p = 0; p < fields.length; p++) {
            for (let t = 0; t < oTypes.length; t++) {
                const exact = this.lookup(fields[p], oTypes[t], topology);
                if (exact !== undefined) {
                    return this.lookupCache.add(exact, key);
                }
            }
        }
        const possibleClasses = this.registeredClasses(fields, topology);
        if (possibleClasses.length === 0) {
            if (topology === DEFAULT_TOPOLOGY.value) {
                return this.lookupCache.add(null, key);
            }
            const foundComponent = this.getRenderComponent(oTypes, fields, DEFAULT_TOPOLOGY.value, defaultType);
            if (!foundComponent) {
                return this.lookupCache.add(null, key);
            }
            return this.lookupCache.add(foundComponent, key);
        }
        for (let i = 0; i < fields.length; i++) {
            const bestClass = this.bestClass(possibleClasses, oTypes);
            const component = bestClass && this.lookup(fields[i], bestClass, topology);
            if (component) {
                return this.lookupCache.add(component, key);
            }
        }
        for (let i = 0; i < fields.length; i++) {
            const component = this.lookup(fields[i], defaultType, topology);
            if (component) {
                return this.lookupCache.add(component, key);
            }
        }
        return this.lookupCache.add(null, key);
    }
    /**
     * Register a renderer for a type/property.
     * @param component The component to return for the rendering of the object.
     * @param type The type's SomeNode of the object which the {component} can render.
     * @param [field] The property's SomeNode if the {component} is a subject renderer.
     * @param [topology] An alternate topology this {component} should render.
     */
    registerRenderer(component, type, field = RENDER_CLASS_NAME$1.value, topology = DEFAULT_TOPOLOGY.value) {
        if (!field || !type) {
            return;
        }
        this.store(component, field, type, topology);
        this.lookupCache.clear();
    }
    /**
     * Find a component from a cache.
     * @param field The property (or {RENDER_CLASS_NAME})
     * @param klass Either the resource type or resource IRI
     * @param topology The topology
     * @param cache The cache to look into (defaults to the mapping)
     * @returns The appropriate component if any
     */
    lookup(field, klass, topology, cache = this.registrations) {
        const predMap = cache[field];
        if (!predMap || !predMap[klass]) {
            return undefined;
        }
        return predMap[klass][topology];
    }
    /** Store a component to a cache. */
    store(component, field, klass, topology, cache = this.registrations) {
        if (typeof cache[field] === "undefined") {
            cache[field] = {};
        }
        if (typeof cache[field][klass] === "undefined") {
            cache[field][klass] = {};
        }
        cache[field][klass][topology] = component;
    }
    /**
     * Expands the given types and returns the best class to render it with.
     * @param components The set of components to choose from.
     * @param [types] The types to expand on.
     * @returns The best match for the given components and types.
     */
    bestClass(components, types) {
        if (types.length > 0) {
            const direct = this.schema.sort(types).find((c) => components.indexOf(c) >= 0);
            if (direct) {
                return direct;
            }
        }
        const chain = this.schema.expand(types ?? []);
        return components.find((c) => chain.indexOf(c) > 0);
    }
    // interface ComponentMapping<T> { [type: string]: { [obj: string]: { [topology: string]: T } }; }
    /**
     * Returns a list of classes which have registrations for a combination of {fields} and {topology}.
     * @private
     */
    registeredClasses(fields, topology) {
        const classes = [Resource.value];
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            if (typeof this.registrations[field] === "undefined") {
                continue;
            }
            const types = Object.keys(this.registrations[field]);
            for (let j = 0; j < types.length; j++) {
                const compType = this.lookup(field, types[j], topology);
                if (compType !== undefined) {
                    classes.push(types[j]);
                }
            }
        }
        return classes;
    }
}

const createEqualComparator = (factory) => factory.supports[Feature.identity]
    ? (a, b) => a === b
    : factory.supports[Feature.idStamp]
        ? (a, b) => a?.id === b?.id
        : (a, b) => factory.equals(a, b);
/** @internal */
const equals = createEqualComparator(rdfFactory);
const noIdError = (obj) => {
    throw new TypeError(`Factory has idStamp feature, but the property wasn't present on ${obj}`);
};
const noValueError = (obj) => {
    throw new TypeError(`Unable to lookup property 'value' on ${obj}.`);
};
/** @internal */
const id = rdfFactory.supports[Feature.idStamp]
    ? (obj) => obj?.id || noIdError(obj)
    : (obj) => rdfFactory.id(obj);
/** @internal */
const value = (obj) => obj?.value ?? noValueError(obj);
const idToValue = (recordId) => {
    if (recordId.includes(":") && !recordId.startsWith("_:")) {
        return rdfFactory.namedNode(recordId);
    }
    else {
        return rdfFactory.blankNode(recordId);
    }
};

/**
 * Tries to schedule updates async if possible.
 */
class ProcessBroadcast {
    constructor(opts) {
        this.hasIdleCallback = "requestIdleCallback" in window;
        this.hasRequestAnimationFrame = "requestAnimationFrame" in window;
        this.resolve = () => undefined;
        this.timeout = opts.timeout;
        this.regUpdateTime = Date.now();
        this.bulkSubscriptions = opts.bulkSubscriptions;
        this.changedSubjects = opts.changedSubjects;
        this.subjectSubscriptions = opts.subjectSubscriptions;
        this.queue = this.queue.bind(this);
    }
    done() {
        return this.subjectSubscriptions.length === 0 && this.bulkSubscriptions.length === 0;
    }
    run() {
        if (this.timeout === 0) {
            this.queue();
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.resolve = resolve;
            this.queue();
        });
    }
    /**
     * Calls the subscriber callback function {reg} with the correct arguments according to its
     * registration settings.
     */
    callSubscriber(reg) {
        if (reg.markedForDelete) {
            return;
        }
        reg.callback(this.changedSubjects, this.regUpdateTime);
    }
    process() {
        if (this.bulkSubscriptions.length > 0) {
            this.callSubscriber(this.bulkSubscriptions.pop());
        }
        else if (this.subjectSubscriptions.length > 0) {
            this.callSubscriber(this.subjectSubscriptions.pop());
        }
    }
    queue(idleCallback) {
        if (this.timeout !== 0 && this.hasIdleCallback) {
            while (typeof idleCallback === "object"
                && (!this.done() && (idleCallback.timeRemaining() > 0 || idleCallback.didTimeout))) {
                this.process();
            }
            if (this.done()) {
                return this.resolve();
            }
            window.requestIdleCallback(this.queue, { timeout: this.timeout });
        }
        else if (this.timeout !== 0 && this.hasRequestAnimationFrame) {
            this.process();
            while (typeof idleCallback === "number" && (performance.now() - idleCallback) < 33) {
                this.process();
            }
            if (this.done()) {
                return this.resolve();
            }
            window.requestAnimationFrame(this.queue);
        }
        else {
            while (!this.done()) {
                this.process();
            }
            this.resolve();
        }
    }
}

const http = createNS("http://www.w3.org/2011/http#");
var http$1 = {
    ns: http,
    /* properties */
    status: http("status"),
    statusCode: http("statusCode"),
};

const httph = createNS("http://www.w3.org/2007/ont/httph#");
var httph$1 = {
    "ns": httph,
    /* properties */
    "Exec-Action": httph("Exec-Action"),
    "date": httph("date"),
    "status": httph("status"),
};

/* global chrome */
const memberPrefix = ns("_").value;
const find = (x, langPrefs) => {
    const language = isLiteral(x) ? x.language : null;
    const index = langPrefs.findIndex((pref) => pref === language);
    return index !== -1 ? index : Infinity;
};
/**
 * Filters {obj} to only include statements where the subject equals {predicate}.
 * @param obj The statements to filter.
 * @param predicate The subject to filter for.
 * @return A possibly empty filtered array of statements.
 */
function allRDFPropertyStatements(obj, predicate) {
    if (typeof obj === "undefined") {
        return [];
    }
    if (rdfFactory.equals(predicate, member$1)) {
        return obj.filter((s) => rdfFactory.equals(s[QuadPosition.predicate], member$1)
            || s[QuadPosition.predicate].value.startsWith(memberPrefix));
    }
    return obj.filter((s) => rdfFactory.equals(s[QuadPosition.predicate], predicate));
}
/**
 * Filters {obj} on subject {predicate} returning the resulting statements' objects.
 * @see allRDFPropertyStatements
 */
function allRDFValues(obj, predicate) {
    const props = allRDFPropertyStatements(obj, predicate);
    if (props.length === 0) {
        return [];
    }
    return props.map((s) => s[QuadPosition.object]);
}
/**
 * Resolve {predicate} to any value, if any. If present, additional values are ignored.
 */
function anyRDFValue(obj, predicate) {
    if (!Array.isArray(obj)) {
        return undefined;
    }
    const match = rdfFactory.equals(predicate, member$1)
        ? obj.find((s) => s[QuadPosition.predicate].value.startsWith(memberPrefix))
        : obj.find((s) => rdfFactory.equals(s[QuadPosition.predicate], predicate));
    if (typeof match === "undefined") {
        return undefined;
    }
    return match[QuadPosition.object];
}
function doc(iri) {
    if (iri.value.includes("#")) {
        return rdfFactory.namedNode(iri.value.split("#").shift());
    }
    return iri;
}
function getPropBestLang(rawProp, langPrefs) {
    if (rawProp.length === 1) {
        return rawProp[0][QuadPosition.object];
    }
    return sortByBestLang(rawProp, langPrefs)[0][QuadPosition.object];
}
function getPropBestLangRaw(statements, langPrefs) {
    if (statements.length === 1) {
        return statements[0];
    }
    return sortByBestLang(statements, langPrefs)[0];
}
function getTermBestLang(rawTerm, langPrefs) {
    if (!Array.isArray(rawTerm)) {
        return rawTerm;
    }
    if (rawTerm.length === 1) {
        return rawTerm[0];
    }
    for (let i = 0; i < langPrefs.length; i++) {
        const pIndex = rawTerm.findIndex((p) => "language" in p && p.language === langPrefs[i]);
        if (pIndex >= 0) {
            return rawTerm[pIndex];
        }
    }
    return rawTerm[0];
}
function sortByBestLang(statements, langPrefs) {
    return statements.sort((a, b) => find(a[QuadPosition.object], langPrefs) - find(b[QuadPosition.object], langPrefs));
}
/**
 * Checks if the origin of {href} matches current origin from {window.location}
 * @returns `true` if matches, `false` otherwise.
 */
function isDifferentOrigin(href) {
    if (typeof href !== "string" && href.termType === TermType.BlankNode) {
        return false;
    }
    const origin = typeof href !== "string" ? href.value : href;
    return !origin.startsWith(self.location.origin + "/");
}
function normalizeType(type) {
    return Array.isArray(type) ? type : [type];
}

/**
 * Extracts the content type from a request.
 * The content-type value has precedence if it contains a known type.
 * Otherwise it returns the extension if present, or the content-type without the encoding.
 *
 * @summary Extracts the content type from a request.
 */
function getContentType(res) {
    const contentTypeRaw = getHeader(res, "Content-Type");
    if (contentTypeRaw === undefined || contentTypeRaw === null) {
        return "";
    }
    const contentType = contentTypeRaw.split(";")[0];
    const url = getURL(res);
    const urlMatch = url && new URL(url).href.match(/\.([a-zA-Z0-9]{1,8})($|\?|#)/);
    const ext = urlMatch ? urlMatch[1] : "";
    if (contentType) {
        const matched = contentTypeByMimeString(contentType, ext);
        if (matched) {
            return matched;
        }
    }
    if (ext && !NON_CONTENT_EXTS.includes(ext)) {
        return contentTypeByExtention(ext);
    }
    return contentTypeRaw.split(";")[0];
}
async function getJSON(res) {
    if (res instanceof Response) {
        return res.json();
    }
    else if (res instanceof XMLHttpRequest) {
        return JSON.parse(res.responseText);
    }
    return JSON.parse(res.body);
}
function getHeader(res, header) {
    if (res instanceof Response) {
        return res.headers.get(header);
    }
    else if (typeof XMLHttpRequest !== "undefined" && res instanceof XMLHttpRequest) {
        return res.getResponseHeader(header) || null;
    }
    else if (res && res.headers) {
        const headerValue = res.headers[header];
        return headerValue || null;
    }
    return null;
}
function getURL(res) {
    if (typeof XMLHttpRequest !== "undefined" && res instanceof XMLHttpRequest) {
        return res.responseURL;
    }
    else if ("requestedURI" in res) {
        return res.requestedURI;
    }
    return res.url;
}
function contentTypeByExtention(ext) {
    if (["ttl"].includes(ext)) {
        return F_TURTLE;
    }
    else if (["ntriples", "nt"].includes(ext)) {
        return F_NTRIPLES;
    }
    else if (["jsonld"].includes(ext)) {
        return F_JSONLD;
    }
    else if (["n3"].includes(ext)) {
        return F_N3;
    }
    return ext;
}
function contentTypeByMimeString(contentType, ext) {
    if (contentType.includes(F_NTRIPLES) || contentType.includes(F_NTRIPLES_DEP)) {
        return F_NTRIPLES;
    }
    else if (contentType.includes(F_PLAIN) && ["ntriples", "nt"].indexOf(ext) >= 0) {
        return F_NTRIPLES;
    }
    else if (contentType.includes(F_TURTLE) || contentType.includes(F_TURTLE_DEP)) {
        return F_TURTLE;
    }
    else if (contentType.includes(F_N3)) {
        return F_N3;
    }
    else if (contentType.includes(F_JSONLD)) {
        return F_JSONLD;
    }
    else if (contentType.includes(F_RDF_XML)) {
        return F_RDF_XML;
    }
    return undefined;
}

var RecordState;
(function (RecordState) {
    RecordState[RecordState["Absent"] = 0] = "Absent";
    RecordState[RecordState["Queued"] = 1] = "Queued";
    RecordState[RecordState["Requested"] = 2] = "Requested";
    RecordState[RecordState["Receiving"] = 3] = "Receiving";
    RecordState[RecordState["Present"] = 4] = "Present";
})(RecordState || (RecordState = {}));

class ProcessorError extends Error {
    constructor(msg, response) {
        super(msg);
        this.response = response;
    }
}

class RequestInitGenerator {
    constructor(opts = {}) {
        this.baseHeaders = opts.headers || {};
        this.csrfFieldName = opts.csrfFieldName ?? "csrf-token";
        this.credentials = opts.credentials ?? "include";
        this.mode = opts.mode ?? "same-origin";
        this.xRequestedWith = opts.xRequestedWith ?? "XMLHttpRequest";
    }
    authenticityHeader(options = {}) {
        return Object.assign({}, options, {
            "X-CSRF-Token": this.getAuthenticityToken(),
            "X-Requested-With": this.xRequestedWith,
        });
    }
    generate(method = "GET", accept = "text/turtle", body) {
        const isFormEncoded = body instanceof URLSearchParams;
        const headers = this.getHeaders(accept);
        if (isFormEncoded) {
            headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
        }
        return {
            body: isFormEncoded ? body.toString() : body,
            credentials: this.credentials,
            headers,
            method: method.toUpperCase(),
            mode: this.mode,
        };
    }
    getHeaders(accept) {
        const acceptHeader = Object.assign({}, this.baseHeaders, { Accept: accept });
        if (this.credentials === "include" || this.credentials === "same-origin") {
            return this.authenticityHeader(acceptHeader);
        }
        return acceptHeader;
    }
    getMetaContent(name) {
        const header = document.head && document.head.querySelector(`meta[name="${name}"]`);
        return header && header.getAttribute("content");
    }
    getAuthenticityToken() {
        return this.getMetaContent(this.csrfFieldName) || "";
    }
}

const SAFE_METHODS = ["GET", "HEAD", "OPTIONS", "CONNECT", "TRACE"];
async function handleStatus(res) {
    if (res.status === NOT_FOUND) {
        throw {
            message: `404: '${getURL(res)}' could not be found`,
            res,
        };
    }
    else if (res.status >= BAD_REQUEST && res.status < INTERNAL_SERVER_ERROR) {
        if ((getHeader(res, "Content-Type") || "").includes("json")) {
            const body = await getJSON(res);
            throw {
                message: "errors" in body ? body.errors && body.errors[0] && body.errors[0].message : "",
                res,
            };
        }
        throw {
            message: `404: '${getURL(res)}' could not be found`,
            res,
        };
    }
    else if (res.status >= INTERNAL_SERVER_ERROR) {
        return Promise.reject({
            message: "Internal server error",
            res,
        });
    }
    return res;
}
/**
 * Pushes in-place value {v} onto an array under key {k} of Map {map}.
 * @param map The reference to the Map to add the data to.
 * @param k The key on {map}. An array is initialized when it doesn't yet exists.
 * @param v The value to push on the array under {k}.
 */
function pushToMap(map, k, v) {
    if (typeof map[k] === "undefined") {
        map[k] = [];
    }
    map[k].push(v);
}
/**
 * Saves response metadata into a graph.
 * @param iri The original iri that was fetched.
 * @param res The (fetch) response object from the request.
 * @returns A graph with metadata about the response.
 */
function processResponse(iri, res) {
    const rawURL = getURL(res);
    const origin = typeof iri === "string"
        ? rdfFactory.namedNode(new URL(rawURL).origin)
        : site(iri);
    if (rawURL && iri !== rawURL) {
        return [
            [
                typeof iri === "string" ? rdfFactory.namedNode(iri) : iri,
                rdfFactory.namedNode("http://www.w3.org/2002/07/owl#sameAs"),
                rdfFactory.namedNode(rawURL),
                origin,
            ],
        ];
    }
    return [];
}
const emptyRequest = Object.freeze({
    lastRequested: null,
    requested: false,
    status: null,
    timesRequested: 0,
});
class DataProcessor {
    constructor(opts) {
        this.deltas = [];
        this.accept = opts.accept || {
            default: "",
        };
        this.bulkEndpoint = opts.bulkEndpoint || `${typeof window !== "undefined" ? window.location.origin : ""}/link-lib/bulk`;
        this._dispatch = opts.dispatch;
        this.invalidationMap = new Map();
        this.requestInitGenerator = opts.requestInitGenerator || new RequestInitGenerator();
        this.mapping = opts.mapping || {};
        this.report = opts.report;
        this.requestMap = new Map();
        this.statusMap = {};
        this.store = opts.store;
        this.fetch = opts.fetch || (typeof window !== "undefined" && typeof fetch !== "undefined"
            ? fetch.bind(window)
            : (_, __) => Promise.reject());
        if (opts.transformers) {
            opts.transformers.forEach((t) => this.registerTransformer(t.transformer, t.mediaType, t.acceptValue));
        }
        this.execExecHeader = this.execExecHeader.bind(this);
        this.processExecAction = this.processExecAction.bind(this);
        this.feedResponse = this.feedResponse.bind(this);
    }
    get dispatch() {
        if (typeof this._dispatch === "undefined") {
            throw new Error("Invariant: cannot call `dispatch` before initialization is complete");
        }
        return this._dispatch;
    }
    set dispatch(value) {
        this._dispatch = value;
    }
    async execActionByIRI(subject, dataTuple) {
        const [graph, blobs = []] = dataTuple;
        if (this.store.getInternalStore().store.getRecord(subject.value) === undefined) {
            await this.getEntity(subject);
        }
        const target$1 = this.store.getResourceProperty(subject, target);
        if (!target$1 || target$1.termType === "Collection" || target$1.termType === TermType.Literal) {
            throw new ProcessorError(MSG_INCORRECT_TARGET);
        }
        const urls = this.store.getResourceProperty(target$1, url);
        const url$1 = Array.isArray(urls) ? urls[0] : urls;
        if (!url$1) {
            throw new ProcessorError(MSG_URL_UNDEFINED);
        }
        if (url$1.termType !== TermType.NamedNode) {
            throw new ProcessorError(MSG_URL_UNRESOLVABLE);
        }
        const targetMethod = this.store.getResourceProperty(target$1, httpMethod);
        const method = typeof targetMethod !== "undefined" ? targetMethod.value : "GET";
        const opts = this.requestInitGenerator.generate(method, this.acceptForHost(url$1));
        if (opts.headers instanceof Headers) {
            opts.headers.set("Request-Referrer", subject.value);
        }
        else if (opts.headers && !Array.isArray(opts.headers)) {
            opts.headers["Request-Referrer"] = subject.value;
        }
        if (!SAFE_METHODS.includes(method) && graph && graph.store.recordCount > 0) {
            if (opts.headers instanceof Headers) {
                opts.headers.delete("Content-Type");
            }
            else if (opts.headers && !Array.isArray(opts.headers)) {
                delete opts.headers["Content-Type"];
            }
            const data = new FormData();
            const rdfSerialization = this.serialize(graph.quads);
            data.append(ll$1.graph.toString(), new Blob([rdfSerialization], { type: F_NTRIPLES }));
            for (let i = 0; i < blobs.length; i++) {
                data.append(blobs[i][0].toString(), blobs[i][1]);
            }
            opts.body = data;
        }
        const resp = await this.fetch(url$1.value, opts).then(this.processExecAction);
        if (resp.status > BAD_REQUEST) {
            // TODO: process responses with a correct content-type.
            throw new ProcessorError(MSG_BAD_REQUEST, resp);
        }
        const statements = await this.feedResponse(resp, true);
        const location = getHeader(resp, "Location");
        const fqLocation = location && new URL(location || "", window.location.origin).toString();
        const iri = fqLocation && rdfFactory.namedNode(fqLocation) || null;
        return {
            data: statements,
            iri,
        };
    }
    flush() {
        const deltas = this.deltas;
        this.deltas = [];
        for (let i = 0; i < deltas.length; i++) {
            try {
                this.processDelta(deltas[i]);
            }
            catch (e) {
                this.report(e);
            }
        }
        return new Set();
    }
    getEntities(resources) {
        const toBeFetched = new Set();
        for (const [iri, fetchOpts] of resources) {
            const rId = id(iri);
            if (fetchOpts && fetchOpts.reload) ;
            else if (this.requestMap.has(rId)) {
                continue;
            }
            toBeFetched.add(iri);
        }
        if (toBeFetched.size === 0) {
            return Promise.resolve([]);
        }
        const body = new URLSearchParams();
        for (const entry of toBeFetched) {
            body.append("resource[]", encodeURIComponent(entry.value));
        }
        const opts = this.requestInitGenerator.generate("POST", this.acceptForHost(this.bulkEndpoint), body);
        const chain = this.fetch(this.bulkEndpoint, opts)
            .then(this.feedResponse)
            .catch((err) => {
            const hasStatus = err && "status" in err;
            this.report(err);
            const status = rdfFactory.literal(hasStatus ? err.status : 499, integer);
            const delta = resources
                .map(([s]) => {
                this.setStatus(s, status);
                return [s, http$1.statusCode, status, ll$1.meta];
            });
            return this.processDelta(delta);
        }).finally(() => {
            toBeFetched.forEach((resource) => this.requestMap.delete(id(resource)));
        });
        toBeFetched.forEach((resource) => {
            this.requestMap.set(id(resource), chain);
            this.setStatus(resource, null);
        });
        return chain;
    }
    /**
     * @see LinkedDataAPI#getEntity
     * @param iri The SomeNode of the entity
     * @param opts The options for fetch-/processing the resource.
     * @param opts The options for fetch-/processing the resource.
     * @return A promise with the resulting entity
     */
    async getEntity(iri, opts) {
        const url = new URL(iri.value);
        url.hash = "";
        const requestIRI = rdfFactory.namedNode(url.toString());
        const existing = this.requestMap.get(requestIRI.id);
        if (existing) {
            return existing;
        }
        let preExistingData = [];
        if (opts && opts.clearPreviousData) {
            preExistingData = this.store.quadsFor(iri);
            preExistingData = preExistingData.concat(this.store.quadsFor(requestIRI));
        }
        try {
            const reqOpts = this.requestInitGenerator.generate("GET", this.acceptForHost(requestIRI));
            const req = this.fetch(requestIRI.value, reqOpts)
                .then((response) => {
                this.setStatus(iri, response.status);
                return response;
            })
                .then(this.feedResponse)
                .catch((err) => {
                this.report(err);
                const hasStatus = err && "status" in err;
                const status = rdfFactory.literal(hasStatus ? err.status : 499, integer);
                this.setStatus(iri, status);
                const delta = [
                    [requestIRI, http$1.statusCode, status, ll$1.meta],
                ];
                return this.processDelta(delta);
            });
            this.requestMap.set(id(requestIRI), req);
            return await req;
        }
        catch (e) {
            if (typeof e.res === "undefined") {
                throw e;
            }
            this.store.removeQuads(preExistingData);
            const responseQuads = processResponse(iri, e.res);
            this.setStatus(iri, 499);
            this.store.addQuads(responseQuads);
            return responseQuads;
        }
        finally {
            this.requestMap.delete(id(requestIRI));
        }
    }
    /**
     * @see LinkedDataAPI#getStatus for documentation
     */
    getStatus(iri) {
        const irl = doc(iri);
        const existing = this.statusMap[irl.value];
        if (existing) {
            return existing;
        }
        return emptyRequest;
    }
    invalidate(iri, _err) {
        const subject = typeof iri === "string" ? rdfFactory.namedNode(iri) : iri;
        this.invalidationMap.set(id(subject));
        this.store.getInternalStore().store.transition(subject.value, RecordState.Absent);
        // TODO: Don't just remove, but rather mark it as invalidated so it's history isn't lost.
        this.clearStatus(subject);
        return true;
    }
    isInvalid(iri) {
        return this.invalidationMap.has(id(iri));
    }
    processExternalResponse(response) {
        return handleStatus(response)
            .then(this.feedResponse);
    }
    processDelta(delta) {
        let s;
        for (let i = 0, len = delta.length; i < len; i++) {
            s = delta[i];
            const subj = s ? s[0] : undefined;
            if (!s || !equals(s[QuadPosition.graph], ll$1.meta)) {
                continue;
            }
            if (equals(s[1], http$1.statusCode)) {
                const subject = subj;
                const status = parseInt(s[2].value, 10);
                this.removeInvalidation(subject);
                if (status >= 200 && status < 400) {
                    this.store.getInternalStore().store.transition(s[0].value, RecordState.Present);
                    this.setStatus(subject, Number.parseInt(s[2].value, 10), false);
                }
                else if (status >= 400 && status < 500) {
                    this.store.getInternalStore().store.deleteRecord(s[0].value);
                    this.store.getInternalStore().store.addField(s[0].value, type.value, ll$1.ErrorResource);
                    this.store.getInternalStore().store.addField(s[0].value, type.value, ll$1.ClientError);
                    this.store.getInternalStore().store.addField(s[0].value, http$1.statusCode.value, rdfFactory.literal(status));
                    this.setStatus(subject, Number.parseInt(s[2].value, 10), false);
                }
                else if (status >= 500 && status < 600) {
                    this.store.getInternalStore().store.deleteRecord(s[0].value);
                    this.store.getInternalStore().store.addField(s[0].value, type.value, ll$1.ErrorResource);
                    this.store.getInternalStore().store.addField(s[0].value, type.value, ll$1.ServerError);
                    this.store.getInternalStore().store.addField(s[0].value, http$1.statusCode.value, rdfFactory.literal(status));
                    this.setStatus(subject, Number.parseInt(s[2].value, 10), false);
                }
                else {
                    this.setStatus(subject, Number.parseInt(s[2].value, 10), false);
                }
            }
            else if (equals(s[1], httph$1["Exec-Action"])) {
                this.execExecHeader(s[2].value);
            }
        }
        return [];
    }
    /** Register a transformer so it can be used to interact with API's. */
    registerTransformer(transformer, mediaType, acceptValue) {
        const mediaTypes = Array.isArray(mediaType) ? mediaType : [mediaType];
        mediaTypes.forEach((type) => {
            pushToMap(this.mapping, type, transformer);
            this.accept.default = [this.accept.default, [type, acceptValue].join(";")].join();
        });
    }
    queueDelta(delta) {
        this.deltas.push(delta);
        const store = this.store.getInternalStore().store;
        const llNS = ll$1.ns("").value;
        const ldNS = ns$1("").value;
        for (const d of delta) {
            if (!d) {
                continue;
            }
            const g = d[QuadPosition.graph];
            if (g.value.startsWith(llNS) || g.value.startsWith(ldNS)) {
                const s = d[QuadPosition.subject].value;
                if (!this.statusMap[s]) {
                    store.transition(s, RecordState.Receiving);
                }
            }
        }
    }
    setAcceptForHost(origin, acceptValue) {
        this.accept[new URL(origin).origin] = acceptValue;
    }
    acceptForHost(iri) {
        return this.accept[new URL(typeof iri === "string" ? iri : iri.value).origin]
            || this.accept.default;
    }
    execExecHeader(actionsHeader, args) {
        if (actionsHeader) {
            const actions = actionsHeader.split(", ");
            for (let i = 0; i < actions.length; i++) {
                this.dispatch(rdfFactory.namedNode(actions[i]), args);
            }
        }
    }
    feedResponse(res, expedite = false) {
        if (res.status >= INTERNAL_SERVER_ERROR) {
            return Promise.reject(res);
        }
        const format = getContentType(res);
        const formatProcessors = this.mapping[format];
        const processor = formatProcessors && formatProcessors[0];
        if (processor === undefined) {
            return Promise.resolve([]);
        }
        res.expedite = expedite;
        return processor(res);
    }
    memoizeStatus(iri, s, transition = true) {
        if (transition) {
            this.store.getInternalStore().store.transition(iri.value, this.requestStatusToJournalStatus(s));
        }
        this.statusMap[iri.value] = s;
        return s;
    }
    clearStatus(iri) {
        this.statusMap[iri.value] = undefined;
    }
    requestStatusToJournalStatus(s) {
        if (s.requested && s.status === null) {
            return RecordState.Requested;
        }
        else if (s.requested) {
            return RecordState.Present;
        }
        else if (!s.requested && s.status === null) {
            return RecordState.Absent;
        }
        else {
            throw new Error(`Unmapped status ${s}`);
        }
    }
    processExecAction(res) {
        const actionsHeader = getHeader(res, "Exec-Action");
        this.execExecHeader(actionsHeader);
        return Promise.resolve(res);
    }
    removeInvalidation(subject) {
        this.invalidationMap.delete(id(subject));
    }
    serialize(data) {
        return data.reduce((acc, qdr) => acc.concat(rdfFactory.toNQ(rdfFactory.quad(qdr[QuadPosition.subject], qdr[QuadPosition.predicate], qdr[QuadPosition.object], qdr[QuadPosition.graph]))), "");
    }
    setStatus(iri, status, transition = true) {
        const url = doc(iri);
        const prevStatus = this.statusMap[url.value];
        this.memoizeStatus(url, {
            lastRequested: new Date(),
            lastResponseHeaders: null,
            requested: true,
            status,
            subject: iri,
            timesRequested: prevStatus ? prevStatus.timesRequested + 1 : 1,
        }, transition);
    }
}

const mergeTerms = (a, b) => {
    if (Array.isArray(a)) {
        return Array.from(new Set([...a, ...normalizeType(b)]));
    }
    else if (a) {
        return Array.from(new Set([a, ...normalizeType(b)]));
    }
    else {
        return b;
    }
};
const isLocalId = (id) => id.startsWith("_:");
const isGlobalId = (id) => id.startsWith("/") || (id.includes(":") && !id.startsWith("_:"));

const doc$1 = (recordId) => {
    return recordId.split("#")[0];
};
const absentStatus = {
    current: RecordState.Absent,
    lastUpdate: -1,
    previous: RecordState.Absent,
};
// tslint:disable member-ordering
class RecordJournal {
    constructor(onChange, data) {
        this.data = {};
        this.onChange = () => undefined;
        this.onChange = onChange;
        if (data) {
            this.data = data;
        }
    }
    copy(onChange = null) {
        return new RecordJournal(onChange ?? this.onChange, JSON.parse(JSON.stringify(this.data)));
    }
    /**
     * Get the [RecordStatus] for the [recordId].
     * Will return an invalid status when passing a local id.
     */
    get(recordId) {
        return this.data[doc$1(recordId)] ?? absentStatus;
    }
    touch(recordId) {
        const docId = doc$1(recordId);
        if (this.data[docId] === undefined) {
            this.data[docId] = {
                current: RecordState.Absent,
                lastUpdate: Date.now(),
                previous: RecordState.Absent,
            };
        }
        else {
            this.data[docId].lastUpdate = Date.now();
        }
        this.onChange(docId);
        this.onChange(recordId);
    }
    transition(recordId, state) {
        const docId = doc$1(recordId);
        const existing = this.data[docId];
        const previous = existing !== undefined
            ? existing.current
            : RecordState.Absent;
        this.data[docId] = {
            current: state,
            lastUpdate: Date.now(),
            previous,
        };
        this.onChange(docId);
        if (docId !== recordId) {
            this.onChange(recordId);
        }
    }
}

const idField = "_id";
const member = member$1.value;
const memberPrefix$1 = ns("_").value;
const namedNode = rdfFactory.namedNode.bind(rdfFactory);
const blankNode = rdfFactory.blankNode.bind(rdfFactory);
const tryParseInt = (value) => {
    try {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? value : parsed;
    }
    catch (e) {
        return value;
    }
};
const tryParseSeqNumber = (value) => {
    const ordinal = value.split(memberPrefix$1).pop();
    if (ordinal !== undefined) {
        return tryParseInt(ordinal);
    }
    else {
        return value;
    }
};
const getSortedFieldMembers = (record) => {
    const values = [];
    const sortedEntries = Object
        .entries(record)
        .sort(([k1], [k2]) => {
        const a = tryParseSeqNumber(k1);
        const b = tryParseSeqNumber(k2);
        if (typeof a !== "string" && typeof b !== "string") {
            return a - b;
        }
        if (typeof a !== "string") {
            return -1;
        }
        return a < b ? -1 : (a > b ? 1 : 0);
    });
    for (const [f, v] of sortedEntries) {
        if (f === member || f.startsWith(memberPrefix$1)) {
            values.push(...normalizeType(v));
        }
    }
    return values;
};
class StructuredStore {
    /**
     *
     * @param base
     * @param data Will be modified, so don't re-use the reference elsewhere.
     * @param onChange @internal
     */
    constructor(base = "rdf:defaultGraph", data = {}, onChange = () => undefined) {
        this.aliases = {};
        this.base = base;
        this.data = data ?? {};
        this.journal = new RecordJournal(onChange);
        for (const key in this.data) {
            if (this.data.hasOwnProperty(key)) {
                this.journal.transition(key, RecordState.Present);
            }
        }
    }
    static toSomeNode(id) {
        if (id.indexOf("_:") === 0) {
            return blankNode(id);
        }
        else {
            return namedNode(id);
        }
    }
    get recordCount() {
        return Object.keys(this.data).length;
    }
    getStatus(recordId) {
        return this.journal.get(this.primary(recordId));
    }
    transition(recordId, state) {
        this.journal.transition(recordId, state);
    }
    touch(recordId) {
        this.journal.touch(recordId);
    }
    deleteRecord(recordId) {
        if (this.data[recordId] === undefined) {
            if (this.journal.get(recordId).current !== RecordState.Absent) {
                this.journal.transition(recordId, RecordState.Absent);
            }
        }
        else {
            this.journal.transition(recordId, RecordState.Absent);
            delete this.data[recordId];
        }
    }
    getField(recordId, field) {
        if (field === member) {
            const record = this.getRecord(recordId);
            if (record === undefined) {
                return undefined;
            }
            return getSortedFieldMembers(record);
        }
        else {
            return this.getRecord(recordId)?.[field];
        }
    }
    /**
     * @returns Whether a mutation has occurred.
     */
    setField(recordId, field, value) {
        if (field === idField) {
            throw new Error("Can't set system fields");
        }
        this.initializeRecord(recordId);
        const current = this.getRecord(recordId);
        if (current[field] === value) {
            return false;
        }
        this.setRecord(recordId, {
            ...current,
            [field]: (Array.isArray(value) && value.length === 1)
                ? value[0]
                : value,
        });
        return true;
    }
    /**
     * Adds a value to a field.
     * @returns Whether a mutation has occurred.
     */
    addField(recordId, field, value) {
        if (field === idField) {
            throw new Error("Can't set system fields");
        }
        this.initializeRecord(recordId);
        const existingRecord = this.getRecord(recordId);
        const existingValue = existingRecord?.[field];
        const existingIsArray = Array.isArray(existingValue);
        const valueAlreadyPresent = existingIsArray
            ? existingValue.includes(value)
            : existingValue !== undefined && existingValue === value;
        if (valueAlreadyPresent) {
            return false;
        }
        const combined = existingIsArray
            ? [...existingValue, value]
            : existingValue
                ? [existingValue, value]
                : value;
        this.setRecord(recordId, {
            ...existingRecord,
            [field]: combined,
        });
        return true;
    }
    deleteField(recordId, field) {
        if (this.getField(recordId, field) === undefined) {
            return;
        }
        const next = {
            ...this.getRecord(recordId),
        };
        delete next[field];
        this.setRecord(recordId, next);
    }
    deleteFieldMatching(recordId, field, value) {
        const current = this.getField(recordId, field);
        if (current === undefined) {
            return;
        }
        if (Array.isArray(current) ? !current.includes(value) : current !== value) {
            return;
        }
        const rest = Array.isArray(current) ? current.filter((s) => s !== value) : undefined;
        if (rest !== undefined && rest.length > 0) {
            this.setField(recordId, field, rest);
        }
        else {
            this.deleteField(recordId, field);
        }
    }
    /**
     * Returns the [Id] which is used to store the data under.
     * @internal
     */
    primary(id) {
        return (this.aliases[id] ?? id);
    }
    /**
     * Find all records which reference this given [recordId]
     */
    references(recordId) {
        const references = [];
        const data = this.data;
        for (const rId in data) {
            if (!data.hasOwnProperty(rId)) {
                continue;
            }
            const record = data[rId];
            for (const field in record) {
                if (!record.hasOwnProperty(field) || field === idField) {
                    continue;
                }
                const values = record[field];
                if (Array.isArray(values)) {
                    for (const value of values) {
                        if (value.termType !== TermType.Literal && value.value === recordId) {
                            references.push(rId);
                        }
                    }
                }
                else {
                    if (values.termType !== TermType.Literal && values.value === recordId) {
                        references.push(rId);
                    }
                }
            }
        }
        return references;
    }
    /**
     * Sets the {previous} aliased to the topmost alias of {current}.
     * Aliasing only applies to record ids.
     * Any previous alias will be ignored, circular aliasing will be ignored.
     * Blank nodes should not be {current}.
     */
    setAlias(previous, current) {
        if (previous === current
            || this.aliases[previous] === current
            || this.aliases[current] === previous) {
            return;
        }
        if (this.aliases[current] !== undefined) {
            this.setAlias(previous, this.aliases[current]);
            return;
        }
        this.aliases[previous] = current;
    }
    allRecords() {
        return Object.values(this.data);
    }
    getRecord(recordId) {
        return this.data[this.primary(recordId)];
    }
    collectRecord(recordId, collected = []) {
        const record = this.getRecord(recordId);
        if (!record) {
            return undefined;
        }
        const unpack = (v) => {
            if (v.termType !== "BlankNode" || collected.includes(v.value)) {
                return v;
            }
            return this.collectRecord(v.value, [v.value, ...collected]) ?? v;
        };
        return Object.entries(record).reduce((acc, [k, v]) => ({
            [k]: (Array.isArray(v) ? v.map(unpack) : unpack(v)),
            ...acc,
        }), { _id: record._id });
    }
    setRecord(recordId, fields) {
        this.initializeRecord(recordId);
        this.data[recordId] = {
            ...fields,
            _id: this.data[recordId]._id,
        };
        this.journal.transition(recordId, RecordState.Present);
        return this.data[recordId];
    }
    initializeRecord(recordId) {
        if (this.data[recordId] === undefined) {
            this.journal.transition(recordId, RecordState.Receiving);
            this.data[recordId] = {
                _id: StructuredStore.toSomeNode(recordId),
            };
        }
    }
}

const EMPTY_ST_ARR = Object.freeze([]);
class RDFAdapter {
    constructor(opts = {}) {
        this.recordCallbacks = [];
        this.store = new StructuredStore("rdf:defaultGraph", opts.data, (recordId) => {
            if (opts.onChange) {
                opts.onChange(recordId);
            }
            this.recordCallbacks.forEach((cb) => cb(recordId));
        });
        this.rdfFactory = opts.rdfFactory ?? rdfFactory;
        opts.quads?.forEach((q) => this.add(q[QuadPosition.subject], q[QuadPosition.predicate], q[QuadPosition.object], q[QuadPosition.graph]));
        this.addRecordCallback(this.handleAlias.bind(this));
        this.storeGraph = this.rdfFactory.namedNode(this.store.base);
    }
    /** @deprecated */
    get quads() {
        const qdrs = [];
        const data = this.store.data;
        for (const recordId in data) {
            if (!data.hasOwnProperty(recordId)) {
                continue;
            }
            qdrs.push(...this.quadsForRecord(recordId));
        }
        return qdrs;
    }
    /** @deprecated */
    add(subject, predicate, object, _graph = this.rdfFactory.defaultGraph()) {
        const asQuadruple = [subject, predicate, object, _graph];
        this.store.addField(subject.value, predicate.value, object);
        return asQuadruple;
    }
    addRecordCallback(callback) {
        this.recordCallbacks.push(callback);
    }
    deleteRecord(subject) {
        this.store.deleteRecord(subject.value);
    }
    /**
     * Remove a quad from the store
     * @deprecated
     */
    remove(st) {
        const value = this.store.getField(st[QuadPosition.subject].value, st[QuadPosition.predicate].value);
        if (value === undefined) {
            throw new Error(`Quad to be removed is not on store: ${st}`);
        }
        this.store.deleteFieldMatching(st[QuadPosition.subject].value, st[QuadPosition.predicate].value, st[QuadPosition.object]);
        return this;
    }
    /** @deprecated */
    removeQuads(quads) {
        // Ensure we don't loop over the array we're modifying.
        const toRemove = quads.slice();
        for (let i = 0; i < toRemove.length; i++) {
            this.remove(toRemove[i]);
        }
        return this;
    }
    /** @deprecated */
    match(aSubject, aPredicate, aObject, justOne = false) {
        const subject = aSubject ? this.primary(aSubject) : null;
        const predicate = aPredicate ? this.primary(aPredicate) : null;
        const object = aObject
            ? (isNode(aObject) ? this.primary(aObject) : aObject)
            : null;
        let quads;
        if (subject && predicate) {
            const value = this.store.getField(subject.value, predicate.value);
            if (Array.isArray(value)) {
                quads = value.map((v) => [subject, predicate, v, this.storeGraph]);
            }
            else if (value) {
                quads = [[subject, predicate, value, this.storeGraph]];
            }
            else {
                quads = EMPTY_ST_ARR;
            }
        }
        else if (subject) {
            quads = this.quadsForRecord(subject.value);
        }
        else {
            quads = this.quads;
        }
        const filter = (q) => (subject === null || q[QuadPosition.subject] === subject)
            && (predicate === null || q[QuadPosition.predicate] === predicate)
            && (object === null || q[QuadPosition.object] === object);
        if (justOne) {
            const res = quads.find(filter);
            return res ? [res] : EMPTY_ST_ARR;
        }
        return quads.filter(filter);
    }
    /** @deprecated */
    quadsForRecord(recordId) {
        const factory = this.rdfFactory;
        const record = this.store.getRecord(recordId);
        if (record === undefined) {
            return EMPTY_ST_ARR;
        }
        const subject = isGlobalId(recordId)
            ? factory.namedNode(recordId)
            : factory.blankNode(recordId);
        const quadruples = [];
        for (const field in record) {
            if (!record.hasOwnProperty(field) || field === idField) {
                continue;
            }
            const value = record[field];
            const fieldTerm = factory.namedNode(field);
            if (Array.isArray(value)) {
                for (const v of value) {
                    quadruples.push([subject, fieldTerm, v, this.storeGraph]);
                }
            }
            else {
                quadruples.push([subject, fieldTerm, value, this.storeGraph]);
            }
        }
        return quadruples;
    }
    /** @private */
    primary(node) {
        const p = this.store.primary(node.value);
        if (isLocalId(p)) {
            return this.rdfFactory.blankNode(p);
        }
        else {
            return this.rdfFactory.namedNode(p);
        }
    }
    handleAlias(recordId) {
        const rawRecord = this.store.data[recordId];
        if (rawRecord === undefined) {
            return;
        }
        const sameAsValue = rawRecord[sameAs.value];
        if (sameAsValue) {
            this.store.setAlias(rawRecord._id.value, (Array.isArray(sameAsValue) ? sameAsValue[0] : sameAsValue).value);
        }
    }
}

const CI_MATCH_PREFIX = 0;
const CI_MATCH_SUFFIX = 1;
/**
 * Expands a property if it's in short-form while preserving long-form.
 * Note: The vocabulary needs to be present in the store prefix mapping
 * @param prop The short- or long-form property
 * @param namespaces Object of namespaces by their abbreviation.
 * @returns The (expanded) property
 */
function expandProperty(prop, namespaces = {}) {
    if (!prop) {
        return prop;
    }
    if (typeof prop !== "string"
        && Object.prototype.hasOwnProperty.call(prop, "termType")
        && prop.termType === "NamedNode") {
        return rdfFactory.namedNode(prop.value);
    }
    if (typeof prop === "object") {
        if (prop.termType === "NamedNode") {
            return rdfFactory.namedNode(prop.value);
        }
        return undefined;
    }
    if (prop.indexOf("/") >= 1) {
        if (prop.startsWith("<") && prop.endsWith(">")) {
            return rdfFactory.namedNode(prop.slice(1, -1));
        }
        return rdfFactory.namedNode(prop);
    }
    const matches = prop.split(":");
    const constructor = namespaces[matches[CI_MATCH_PREFIX]]?.ns;
    return constructor && constructor(matches[CI_MATCH_SUFFIX]);
}

const BASE = 36;
const DEC_CUTOFF = 2;
const IRI_LEN = 20;
function isPlainObject(o) {
    return typeof o === "object"
        && o !== null
        && !NON_DATA_OBJECTS_CTORS.find((c) => typeof o.prototype !== "undefined" && o instanceof c)
        && !Object.prototype.hasOwnProperty.call(o, "termType");
}
function isIterable(o) {
    return Array.isArray(o) || o instanceof Set;
}
function uploadIRI() {
    return ll$1.ns(`blobs/a${Math.random().toString(BASE).substr(DEC_CUTOFF, IRI_LEN)}`);
}
/**
 * Converts an array to an RDF list-shaped {DataObject} for serialization.
 */
function list(arr) {
    // @ts-ignore
    return arr.reduceRight((acc, next) => ({
        [first.toString()]: next,
        [rest.toString()]: acc,
    }), nil);
}
/**
 * Converts an array to an RDF sequence-shaped {DataObject} for serialization.
 */
function seq(arr, id) {
    const base = { [type.toString()]: Seq };
    if (id) {
        base["@id"] = id;
    }
    return arr.reduce((acc, next, n) => Object.assign(acc, { [ns(`_${n}`).toString()]: next }), base);
}
const isFile = (value) => typeof File !== "undefined" && value instanceof File;
/** @private */
function processObject(subject, predicate, datum, store, ns) {
    let blobs = [];
    if (isIterable(datum)) {
        for (const subResource of datum) {
            blobs = blobs.concat(processObject(subject, predicate, subResource, store, ns));
        }
    }
    else if (typeof datum === "string"
        || typeof datum === "number"
        || typeof datum === "boolean"
        || datum instanceof Date) {
        store.store.addField(subject.value, predicate.value, rdfFactory.literal(datum));
    }
    else if (isFile(datum)) {
        const f = uploadIRI();
        blobs.push([f, datum]);
        store.store.addField(subject.value, predicate.value, f);
    }
    else if (isPlainObject(datum)) {
        const id = datum["@id"] || rdfFactory.blankNode();
        blobs = blobs.concat(processDataObject(id, datum, store, ns));
        store.store.addField(subject.value, predicate.value, id);
    }
    else if (datum && datum.termType === TermType.NamedNode) {
        store.store.addField(subject.value, predicate.value, rdfFactory.namedNode(datum.value));
    }
    else if (datum && datum.termType === TermType.Literal) {
        store.store.addField(subject.value, predicate.value, rdfFactory.literal(datum.value, datum.language || rdfFactory.namedNode(datum.datatype.value)));
    }
    else if (datum !== null && datum !== undefined) {
        store.add(subject, predicate, rdfFactory.literal(datum));
    }
    return blobs;
}
function processDataObject(subject, data, store, ns) {
    let blobs = [];
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
        if (keys[i] === "@id") {
            continue;
        }
        const predicate = expandProperty(keys[i], ns || {});
        const datum = data[keys[i]];
        if (predicate === undefined) {
            throw new Error(`Unknown predicate ${keys[i]} given (for subject '${subject}').`);
        }
        blobs = blobs.concat(processObject(subject, predicate, datum, store, ns));
    }
    return blobs;
}
function dataToGraphTuple(data, ns) {
    const store = new RDFAdapter();
    const blobs = processDataObject(MAIN_NODE_DEFAULT_IRI, data, store, ns);
    return [store, blobs];
}
/**
 * Convert a DataObject into a graph. Useful for writing test data in semi-plain JS objects
 * @param iriOrData The data object or an iri for the top-level object.
 * @param data The data object if an IRI was passed.
 * @param store A graph to write the statements into.
 * @param ns Namespace mapping for converting shortened keys.
 */
function toGraph(iriOrData, data, store, ns) {
    const passedIRI = iriOrData.termType === TermType.BlankNode || iriOrData.termType === TermType.NamedNode;
    if (passedIRI && !data) {
        throw new TypeError("Only an IRI was passed to `toObject`, a valid data object has to be the second argument");
    }
    const embeddedIRI = (passedIRI ? data : iriOrData)["@id"];
    let iri;
    if (embeddedIRI) {
        if (typeof embeddedIRI !== "string") {
            throw new TypeError("Embedded IRI (`@id`) value must be of type string");
        }
        iri = rdfFactory.namedNode(embeddedIRI);
    }
    else {
        iri = passedIRI ? iriOrData : rdfFactory.blankNode();
    }
    const dataObj = passedIRI ? data : iriOrData;
    const s = store || new RDFAdapter();
    const blobs = processDataObject(iri, dataObj, s, ns);
    return [iri, s, blobs];
}

const matchSingle = (graphIRI) => {
    const value = graphIRI.value;
    return (graph) => equals(graph, graphIRI) || graph.value.startsWith(value);
};
const isInGraph = (graphIRIS) => {
    if (graphIRIS.length === 0) {
        throw new Error("Pass a default graph explicitly");
    }
    const matchers = graphIRIS.map((iri) => matchSingle(iri));
    return (graph) => matchers.some((matcher) => matcher(graph));
};
const pushQuadruple = (arr, quadruple, graph) => {
    arr.push([
        quadruple[QuadPosition.subject],
        quadruple[QuadPosition.predicate],
        quadruple[QuadPosition.object],
        graph,
    ]);
};
const deltaProcessor = (addGraphIRIS, replaceGraphIRIS, removeGraphIRIS, purgeGraphIRIS, sliceGraphIRIS) => {
    const defaultGraph = rdfFactory.defaultGraph();
    const isAdd = isInGraph(addGraphIRIS);
    const isReplace = isInGraph(replaceGraphIRIS);
    const isRemove = isInGraph(removeGraphIRIS);
    const isPurge = isInGraph(purgeGraphIRIS);
    const isSlice = isInGraph(sliceGraphIRIS);
    return (store) => (delta) => {
        const addable = [];
        const replaceable = [];
        const removable = [];
        let quad;
        for (let i = 0, len = delta.length; i < len; i++) {
            quad = delta[i];
            if (!quad) {
                continue;
            }
            const g = new URL(quad[QuadPosition.graph].value).searchParams.get("graph");
            const graph = g ? rdfFactory.termFromNQ(g) : defaultGraph;
            if (isAdd(quad[QuadPosition.graph])) {
                pushQuadruple(addable, quad, graph);
            }
            else if (isReplace(quad[QuadPosition.graph])) {
                pushQuadruple(replaceable, quad, graph);
            }
            else if (isRemove(quad[QuadPosition.graph])) {
                removable.push(...store.match(quad[QuadPosition.subject], quad[QuadPosition.predicate], null));
            }
            else if (isPurge(quad[QuadPosition.graph])) {
                removable.push(...store.match(quad[QuadPosition.subject], null, null));
            }
            else if (isSlice(quad[QuadPosition.graph])) {
                removable.push(...store.match(quad[QuadPosition.subject], quad[QuadPosition.predicate], quad[QuadPosition.object]));
            }
        }
        return [
            addable,
            replaceable,
            removable,
        ];
    };
};

const EMPTY_ST_ARR$1 = Object.freeze([]);
/**
 * Provides a clean consistent interface to stored (RDF) data.
 */
class RDFStore {
    constructor({ data, deltaProcessorOpts, innerStore } = {}) {
        this.langPrefs = Array.from(typeof navigator !== "undefined"
            ? (navigator.languages || [navigator.language])
            : ["en"]);
        this.changedResources = new Set();
        this.deltas = [];
        this.store = new RDFAdapter({
            onChange: this.handleChange.bind(this),
        });
        this.defaultGraph = this.rdfFactory.defaultGraph();
        this.processDelta = this.processDelta.bind(this);
        this.store = innerStore || new RDFAdapter({ data, onChange: this.handleChange.bind(this) });
        const defaults = {
            addGraphIRIS: [ll$1.add, add],
            purgeGraphIRIS: [ll$1.purge, purge],
            removeGraphIRIS: [ll$1.remove, remove],
            replaceGraphIRIS: [
                ll$1.replace,
                replace,
                rdfFactory.defaultGraph(),
            ],
            sliceGraphIRIS: [ll$1.slice, slice],
        };
        this.deltaProcessor = deltaProcessor(deltaProcessorOpts?.addGraphIRIS || defaults.addGraphIRIS, deltaProcessorOpts?.replaceGraphIRIS || defaults.replaceGraphIRIS, deltaProcessorOpts?.removeGraphIRIS || defaults.removeGraphIRIS, deltaProcessorOpts?.purgeGraphIRIS || defaults.purgeGraphIRIS, deltaProcessorOpts?.sliceGraphIRIS || defaults.sliceGraphIRIS)(this.store);
    }
    get rdfFactory() {
        return rdfFactory;
    }
    set rdfFactory(_) {
        throw new Error("Factory is global (see @ontologies/core)");
    }
    /** @deprecated */
    add(subject, predicate, object) {
        return this.store.add(subject, predicate, object);
    }
    /**
     * Add statements to the store.
     * @param data Data to parse and add to the store.
     * @deprecated
     */
    addQuads(data) {
        if (!Array.isArray(data)) {
            throw new TypeError("An array of quads must be passed to addQuads");
        }
        for (const q of data) {
            this.store.store.addField(q[QuadPosition.subject].value, q[QuadPosition.predicate].value, q[QuadPosition.object]);
        }
    }
    /** @deprecated */
    addQuadruples(data) {
        const statements = new Array(data.length);
        for (let i = 0, len = data.length; i < len; i++) {
            statements[i] = this.store.add(data[i][0], data[i][1], data[i][2], data[i][3]);
        }
        return statements;
    }
    /** @deprecated */
    primary(term) {
        return this.store.primary(term);
    }
    /**
     * Flushes the change buffer to the return value.
     * @return Statements held in memory since the last flush.
     */
    flush() {
        const deltas = this.deltas;
        this.deltas = [];
        for (let i = 0; i < deltas.length; i++) {
            this.processDelta(deltas[i]);
        }
        const changes = this.changedResources;
        this.changedResources = new Set();
        return changes;
    }
    /** @private */
    getInternalStore() {
        return this.store;
    }
    references(recordId) {
        return this.store.store.references(recordId.value);
    }
    /** @deprecated */
    match(subj, pred, obj, justOne = false) {
        return this.store.match(subj, pred, obj, justOne) ?? EMPTY_ST_ARR$1;
    }
    processDelta(delta) {
        const [addables, replacables, removables,] = this.deltaProcessor(delta);
        this.removeQuads(removables);
        return this.replaceMatches(replacables).concat(this.addQuadruples(addables));
    }
    removeResource(subject) {
        this.touch(subject);
        this.store.deleteRecord(subject);
    }
    /** @deprecated */
    removeQuads(statements) {
        this.store.removeQuads(statements);
    }
    /** @deprecated */
    replaceMatches(statements) {
        for (let i = 0; i < statements.length; i++) {
            this.removeQuads(this.match(statements[i][0], statements[i][1], null));
        }
        return this.addQuadruples(statements);
    }
    getResourcePropertyRaw(subject, property) {
        const properties = normalizeType(property);
        const matched = [];
        for (const prop of properties) {
            const quads = normalizeType(this.store.store.getField(subject.value, prop.value))
                .filter((v) => v !== undefined)
                .map((v) => [subject, prop, v, this.defaultGraph]);
            matched.push(...quads);
        }
        if (matched.length === 0) {
            return EMPTY_ST_ARR$1;
        }
        return sortByBestLang(matched, this.langPrefs);
    }
    getResourceProperties(subject, property) {
        if (property === type) {
            const value = this.store.store.getField(subject.value, type.value);
            if (!value) {
                return EMPTY_ST_ARR$1;
            }
            else {
                return normalizeType(value);
            }
        }
        const properties = normalizeType(property);
        const matched = [];
        for (const prop of properties) {
            const quads = normalizeType(this.store.store.getField(subject.value, prop.value))
                .filter((v) => v !== undefined);
            matched.push(...quads);
        }
        if (matched.length === 0) {
            return EMPTY_ST_ARR$1;
        }
        return matched;
    }
    getResourceProperty(subject, property) {
        if (!Array.isArray(property) && equals(property, type)) {
            return this.store.store.getField(subject.value, type.value);
        }
        const rawProp = this.getResourcePropertyRaw(subject, property);
        if (rawProp.length === 0) {
            return undefined;
        }
        return getPropBestLang(rawProp, this.langPrefs);
    }
    queueDelta(delta) {
        this.deltas.push(delta);
    }
    /**
     * Searches the store for all the quads on {iri} (so not all statements relating to {iri}).
     * @param subject The identifier of the resource.
     */
    quadsFor(subject) {
        return this.store.quadsForRecord(subject.value);
    }
    touch(iri) {
        this.store.store.touch(iri.value);
    }
    workAvailable() {
        return this.deltas.length + this.changedResources.size;
    }
    handleChange(docId) {
        this.changedResources.add(docId);
    }
}

const nsOWLsameAs = sameAs.value;
const OWL = {
    axioms: [],
    processStatement(recordId, field, value, ctx) {
        if (field === nsOWLsameAs && recordId !== value.value) {
            const a = ctx.equivalenceSet.add(value.value);
            const b = ctx.equivalenceSet.add(recordId);
            ctx.equivalenceSet.union(a, b);
        }
    },
    processType(_, __) {
        return false;
    },
};

const defaultGraph = rdfFactory.defaultGraph();
/**
 * Implements the RDF/RDFS axioms and rules.
 */
const RDFS = {
    axioms: [
        [type, domain, Resource, defaultGraph],
        [domain, domain, Property, defaultGraph],
        [range, domain, Property, defaultGraph],
        [subPropertyOf, domain, Property, defaultGraph],
        [subClassOf, domain, Class, defaultGraph],
        [subject, domain, Statement, defaultGraph],
        [predicate, domain, Statement, defaultGraph],
        [object, domain, Statement, defaultGraph],
        [member$1, domain, Resource, defaultGraph],
        [first, domain, List, defaultGraph],
        [rest, domain, List, defaultGraph],
        [seeAlso, domain, Resource, defaultGraph],
        [isDefinedBy, domain, Resource, defaultGraph],
        [comment, domain, Resource, defaultGraph],
        [label, domain, Resource, defaultGraph],
        [value$1, domain, Resource, defaultGraph],
        [type, range, Class, defaultGraph],
        [domain, range, Class, defaultGraph],
        [range, range, Class, defaultGraph],
        [subPropertyOf, range, Property, defaultGraph],
        [subClassOf, range, Class, defaultGraph],
        [subject, range, Resource, defaultGraph],
        [predicate, range, Resource, defaultGraph],
        [object, range, Resource, defaultGraph],
        [member$1, range, Resource, defaultGraph],
        [first, range, Resource, defaultGraph],
        [rest, range, List, defaultGraph],
        [seeAlso, range, Resource, defaultGraph],
        [isDefinedBy, range, Resource, defaultGraph],
        [comment, range, Literal, defaultGraph],
        [label, range, Literal, defaultGraph],
        [value$1, range, Resource, defaultGraph],
        [Alt, subClassOf, Container, defaultGraph],
        [Bag, subClassOf, Container, defaultGraph],
        [Seq, subClassOf, Container, defaultGraph],
        [ContainerMembershipProperty, subClassOf, Property, defaultGraph],
        [isDefinedBy, subPropertyOf, seeAlso, defaultGraph],
        [Datatype, subClassOf, Class, defaultGraph],
        [Resource, type, Class, defaultGraph],
        [Class, type, Class, defaultGraph],
    ],
    processStatement(recordId, field, value, ctx) {
        switch (field) {
            case subClassOf.value: { // C1 rdfs:subClassOf C2
                const objectType = value.termType;
                if (!(objectType === TermType.NamedNode || objectType === TermType.BlankNode)) {
                    throw new Error("Object of subClassOf statement must be a NamedNode");
                }
                const iSubject = recordId;
                const iObject = value.value;
                if (!ctx.superMap.has(iObject)) {
                    ctx.superMap.set(iObject, new Set([Resource.value]));
                }
                let parents = ctx.superMap.get(iObject);
                if (parents === undefined) {
                    parents = new Set();
                    ctx.superMap.set(iObject, parents);
                }
                parents.add(iObject);
                const itemVal = ctx.superMap.get(iSubject) || new Set([iSubject]);
                parents.forEach((i) => itemVal.add(i));
                ctx.superMap.set(iSubject, itemVal);
                ctx.superMap.forEach((v, k) => {
                    if (k !== iSubject && v.has(iSubject)) {
                        itemVal.forEach(v.add, v);
                    }
                });
                break;
            }
        }
    },
    processType(type, ctx) {
        RDFS.processStatement(type, subClassOf.value, Resource, ctx);
        return false;
    },
};

/**
 * DisjointSet
 * Module port from https://github.com/mljs/disjoint-set/
 */
/**
 * @class DisjointSet
 */
class DisjointSet {
    constructor() {
        this.nodes = new Map();
    }
    /**
     * Adds an element as a new set
     * @param {*} value
     * @return {DisjointSetNode} Object holding the element
     */
    add(value) {
        let node = this.nodes.get(value);
        if (!node) {
            node = new DisjointSetNode(value);
            this.nodes.set(value, node);
        }
        return node;
    }
    allValues(value) {
        const node = this.nodes.get(value);
        if (!node) {
            return [value];
        }
        let currentParent = this.find(node);
        const entries = this.nodes.entries();
        const parents = [];
        const values = [];
        const process = (obj) => {
            for (const [k, v] of entries) {
                if (v.parent === obj && !parents.includes(v)) {
                    values.push(k);
                    parents.push(v);
                }
            }
        };
        process(currentParent);
        for (let i = 0; i < parents.length; i++) {
            currentParent = parents[i];
            process(currentParent);
        }
        return values;
    }
    /**
     * Merges the sets that contain x and y
     * @param {DisjointSetNode} x
     * @param {DisjointSetNode} y
     */
    union(x, y) {
        const rootX = this.find(x);
        const rootY = this.find(y);
        if (rootX === rootY) {
            return;
        }
        if (rootX.rank < rootY.rank) {
            rootX.parent = rootY;
        }
        else if (rootX.rank > rootY.rank) {
            rootY.parent = rootX;
        }
        else {
            rootY.parent = rootX;
            rootX.rank++;
        }
    }
    /**
     * Finds and returns the root node of the set that contains node
     * @param {DisjointSetNode} node
     * @return {DisjointSetNode}
     */
    find(node) {
        let rootX = node;
        while (rootX.parent !== null) {
            rootX = rootX.parent;
        }
        let toUpdateX = node;
        while (toUpdateX.parent !== null) {
            const toUpdateParent = toUpdateX;
            toUpdateX = toUpdateX.parent;
            toUpdateParent.parent = rootX;
        }
        return rootX;
    }
    /**
     * Returns true if x and y belong to the same set
     * @param {DisjointSetNode} x
     * @param {DisjointSetNode} y
     */
    connected(x, y) {
        return this.find(x) === this.find(y);
    }
}
// tslint:disable-next-line max-classes-per-file
class DisjointSetNode {
    constructor(value) {
        this.value = value;
        this.parent = null;
        this.rank = 0;
    }
}

/**
 * Implements some RDF/OWL logic to enhance the functionality of the property lookups.
 *
 * Basically duplicates some functionality already present in {IndexedFormula} IIRC, but this API should be more
 * optimized so it can be used in real-time by low-power devices as well.
 */
class Schema {
    constructor(liveStore) {
        this.equivalenceSet = new DisjointSet();
        this.superMap = new Map();
        this.processedTypes = [];
        this.liveStore = liveStore;
        this.liveStore.getInternalStore().addRecordCallback((recordId) => {
            const record = this.liveStore.getInternalStore().store.getRecord(recordId);
            if (record === undefined) {
                return;
            }
            this.process.call(this, record);
        });
        this.expansionCache = {};
        for (let i = 0; i < Schema.vocabularies.length; i++) {
            this.liveStore.addQuads(Schema.vocabularies[i].axioms);
        }
        const preexisting = liveStore.getInternalStore().store.allRecords();
        for (const record of preexisting) {
            this.process(record);
        }
    }
    allEquals(recordId, grade = 1.0) {
        if (grade >= 0) {
            return this.equivalenceSet.allValues(recordId);
        }
        return [recordId];
    }
    /** @private */
    isInstanceOf(recordId, klass) {
        const type$1 = this.liveStore.getInternalStore().store.getField(recordId, type.value);
        if (type$1 === undefined) {
            return false;
        }
        const allCheckTypes = this.expand([klass]);
        const allRecordTypes = this.expand(Array.isArray(type$1) ? type$1.map((t) => t.value) : [type$1.value]);
        return allRecordTypes.some((t) => allCheckTypes.includes(t));
    }
    expand(types) {
        if (types.length === 1) {
            const existing = this.expansionCache[types[0]];
            this.expansionCache[types[0]] = existing
                ? existing
                : this.sort(this.mineForTypes(types));
            return this.expansionCache[types[0]];
        }
        return this.sort(this.mineForTypes(types));
    }
    getProcessingCtx() {
        return {
            dataStore: this.liveStore,
            equivalenceSet: this.equivalenceSet,
            store: this,
            superMap: this.superMap,
        };
    }
    /**
     * Expands the given lookupTypes to include all their equivalent and subclasses.
     * This is done in multiple iterations until no new types are found.
     * @param lookupTypes The types to look up. Once given, these are assumed to be classes.
     */
    mineForTypes(lookupTypes) {
        if (lookupTypes.length === 0) {
            return [Resource.value];
        }
        const canonicalTypes = [];
        const lookupTypesExpanded = [];
        for (let i = 0; i < lookupTypes.length; i++) {
            lookupTypesExpanded.push(...this.allEquals(lookupTypes[i]));
        }
        for (let i = 0; i < lookupTypesExpanded.length; i++) {
            const canon = this.liveStore.getInternalStore().store.primary(lookupTypes[i]);
            if (!this.processedTypes.includes(canon)) {
                for (let j = 0; j < Schema.vocabularies.length; j++) {
                    Schema.vocabularies[j].processType(canon, this.getProcessingCtx());
                }
                this.processedTypes.push(canon);
            }
            if (!canonicalTypes.includes(canon)) {
                canonicalTypes.push(canon);
            }
        }
        const allTypes = canonicalTypes
            .reduce((a, b) => {
            const superSet = this.superMap.get(b);
            if (typeof superSet === "undefined") {
                return a;
            }
            superSet.forEach((s) => {
                if (!a.includes(s)) {
                    a.push(s);
                }
            });
            return a;
        }, [...lookupTypes]);
        return this.sort(allTypes);
    }
    sort(types) {
        return types.sort((a, b) => {
            if (this.isSubclassOf(a, b)) {
                return -1;
            }
            else if (this.isSubclassOf(b, a)) {
                return 1;
            }
            const aDepth = this.superTypeDepth(a);
            const bDepth = this.superTypeDepth(b);
            if (aDepth < bDepth) {
                return 1;
            }
            else if (aDepth > bDepth) {
                return -1;
            }
            return 0;
        });
    }
    /**
     * Returns the hierarchical depth of the type, or -1 if unknown.
     * @param type the type to check
     */
    superTypeDepth(type) {
        const superMap = this.superMap.get(type);
        return superMap ? superMap.size : -1;
    }
    isSubclassOf(resource, superClass) {
        const resourceMap = this.superMap.get(resource);
        if (resourceMap) {
            return resourceMap.has(superClass);
        }
        return false;
    }
    process(record) {
        for (let i = 0; i < Schema.vocabularies.length; i++) {
            for (const [field, values] of Object.entries(record)) {
                for (const value of normalizeType(values)) {
                    Schema.vocabularies[i].processStatement(record._id.value, field, value, this.getProcessingCtx());
                }
            }
        }
    }
}
Schema.vocabularies = [OWL, RDFS];

// @ts-ignore Used when looking up values.
class AttributeKey {
    constructor(key) {
        this.key = key;
    }
    toString() {
        return this.key;
    }
}
// tslint:disable-next-line:max-classes-per-file
class TypedRecord {
    constructor() {
        this.records = new Map();
    }
    get(key) {
        return this.records.get(key);
    }
    get [Symbol.toStringTag]() {
        return this.records[Symbol.toStringTag];
    }
    get size() {
        return this.records.size;
    }
    [Symbol.iterator]() {
        return this.records[Symbol.iterator]();
    }
    clear() {
        this.records.clear();
    }
    delete(key) {
        return this.records.delete(key);
    }
    entries() {
        return this.records.entries();
    }
    forEach(callbackfn, thisArg) {
        return this.records.forEach(callbackfn, thisArg);
    }
    has(key) {
        return this.records.has(key);
    }
    keys() {
        return this.records.keys();
    }
    set(key, value) {
        this.records.set(key, value);
        return this;
    }
    values() {
        return this.records.values();
    }
}

const normalizedIds = (item, defaultValue) => normalizeType(item)
    .map((t) => (t ?? defaultValue).value);
/**
 * Main entrypoint into the functionality of link-lib.
 *
 * Before using the methods for querying data and views here, search through your render library (e.g. link-redux) to
 * see if it exposes an API which covers your use-case. Skipping the render library might cause unexpected behaviour and
 * hard to solve bugs.
 */
class LinkedRenderStore {
    // tslint:disable-next-line no-object-literal-type-assertion
    constructor(opts = {}) {
        /**
         * Map of {ActionMap} which hold action dispatchers. Calling a dispatcher should execute the action, causing them to
         * be handled like any back-end sent action.
         *
         * Constructing action IRI's and dispatching them in user code was a bit hard, this object allows any middleware to
         * define their actions (within their namespace) in a code-oriented fashion. The middleware has control over how the
         * actions will be dispatched, but it should be the same as if a back-end would have executed the action (via the
         * Exec-Action header).
         */
        this.actions = new TypedRecord();
        /** Whenever a resource has no type, assume it to be this. */
        this.defaultType = Thing;
        /**
         * Can aid in parsing and creating prefix mapping strings.
         * @deprecated Please use @ontologies/<namespace> packages in your programs, canonicalizing certain
         *   prefixes will lead to brittle and hard to refactor code!
         */
        this.namespaces = {};
        this.settings = new TypedRecord();
        /**
         * Enable the bulk api to fetch data with.
         */
        this.bulkFetch = false;
        this.cleanupTimout = 500;
        this.bulkSubscriptions = [];
        this.subjectSubscriptions = {};
        this.resourceQueueFlushTimer = 100;
        if (opts.store) {
            this.store = opts.store;
        }
        else {
            this.store = new RDFStore({ data: opts.data });
        }
        this.report = opts.report || ((e) => { throw e; });
        this.api = opts.api || new DataProcessor({
            ...opts.apiOpts,
            dispatch: opts.dispatch,
            report: this.report,
            store: this.store,
        });
        this.deltaProcessors = [this.api, this.store];
        if (opts.dispatch) {
            this.dispatch = opts.dispatch;
        }
        this.defaultType = opts.defaultType || Thing;
        this.namespaces = opts.namespaces || {};
        this.schema = opts.schema || new Schema(this.store);
        this.mapping = opts.mapping || new ComponentStore(this.schema);
        this.resourceQueue = [];
        this.broadcast = this.broadcast.bind(this);
        this.processResourceQueue = this.processResourceQueue.bind(this);
    }
    static registerRenderer(component, type, prop = RENDER_CLASS_NAME, topology = DEFAULT_TOPOLOGY) {
        const types = normalizedIds(type);
        const props = normalizedIds(prop, RENDER_CLASS_NAME);
        const topologies = normalizedIds(topology, DEFAULT_TOPOLOGY);
        return ComponentStore.registerRenderer(component, types, props, topologies);
    }
    get dispatch() {
        if (typeof this._dispatch === "undefined") {
            throw new Error("Invariant: cannot call `dispatch` before initialization is complete, see createStore");
        }
        return this._dispatch;
    }
    set dispatch(v) {
        this._dispatch = v;
        this.api.dispatch = v;
    }
    /**
     * Add a custom delta processor in the stack.
     */
    addDeltaProcessor(processor) {
        this.deltaProcessors.unshift(processor);
    }
    /**
     * Execute an Action by its IRI. This will result in an HTTP request being done and probably some state changes.
     * @param subject The resource to execute. Generally a schema:Action derivative with a
     *   schema:EntryPoint to describe the request. Currently schema:url is used over schema:urlTemplate
     *   to acquire the request URL, since template processing isn't implemented (yet).
     * @param data An object to send in the body when a non-safe method is used.
     */
    execActionByIRI(subject, data) {
        const preparedData = dataToGraphTuple(data || {}, this.namespaces);
        return this
            .api
            .execActionByIRI(subject, preparedData)
            .then((res) => {
            this.broadcast(false, 100);
            return res;
        });
    }
    /**
     * Execute a resource.
     *
     * Every action will fall through the execution middleware layers.
     *
     * @see https://github.com/rescribet/link-lib/wiki/%5BDesign-draft%5D-Actions,-data-streams,-and-middleware
     *
     * @param subject The resource to execute (can be either an IRI or an URI)
     * @param args The arguments to the function defined by the subject.
     */
    async exec(subject, args) {
        return this.dispatch(subject, args);
    }
    /**
     * Resolve the values at the end of the path
     * @param subject The resource to start descending on
     * @param path A list of linked predicates to descend on.
     */
    dig(subject, path) {
        const [result] = this.digDeeper(subject, path);
        return result.map((q) => q[QuadPosition.object]);
    }
    /**
     * @internal See {dig}
     * @param subject
     * @param path
     * @param subject - The subject traversed.
     */
    digDeeper(subject, path) {
        if (path.length === 0 || typeof subject === "undefined") {
            return [[], []];
        }
        const last = path.length - 1;
        let ids = [subject];
        const intermediates = [subject];
        const values = [];
        for (let i = 0; i <= last; i++) {
            const field = path[i];
            const segmentIds = ids;
            ids = [];
            for (let j = 0; j < segmentIds.length; j++) {
                const id = segmentIds[j];
                const quads = this.getResourcePropertyRaw(id, field);
                if (i === last) {
                    values.push(...quads);
                    continue;
                }
                const next = quads
                    .map((q) => q[QuadPosition.object])
                    .filter((v) => isNode(v));
                intermediates.push(...next);
                ids.push(...next);
            }
        }
        return [values, intermediates];
    }
    /**
     * Retrieve the subjects from {subject} to find all resources which have an object at the
     * end of the {path} which matches {match}.
     * @param subject The resource to start descending on.
     * @param path A list of linked predicates to descend on.
     * @param match The value which the predicate at the end of {path} has to match for its subject to return.
     */
    findSubject(subject, path, match) {
        if (path.length === 0 || typeof subject === "undefined") {
            return [];
        }
        const remaining = path.slice();
        const pred = remaining.shift();
        const props = this.getResourceProperties(subject, pred);
        if (props.length === 0) {
            return [];
        }
        if (remaining.length === 0) {
            const finder = Array.isArray(match)
                ? (p) => match.some((m) => equals(m, p))
                : (p) => equals(match, p);
            return props.find(finder) ? [subject] : [];
        }
        return props
            .map((term) => (term.termType === TermType.NamedNode || term.termType === TermType.BlankNode)
            && this.findSubject(term, remaining, match))
            .flat(1)
            .filter(Boolean);
    }
    /**
     * Finds the best render component for a given property in respect to a topology.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param type The type(s) of the resource to render.
     * @param predicate The predicate(s) (property(s)) to render.
     * @param [topology] The topology of the resource, if any
     * @returns The most appropriate renderer, if any.
     */
    getComponentForProperty(type = this.defaultType, predicate, topology = DEFAULT_TOPOLOGY) {
        if (type === undefined || (Array.isArray(type) && type.length === 0)) {
            return undefined;
        }
        const types = normalizeType(type).map(value);
        const predicates = normalizeType(predicate).map(value);
        return this.mapping.getRenderComponent(types, predicates, value(topology), value(this.defaultType));
    }
    /**
     * Finds the best render component for a type in respect to a topology.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @see LinkedRenderStore#getComponentForProperty
     * @param type The type(s) of the resource to render.
     * @param [topology] The topology of the resource, if any
     * @returns The most appropriate renderer, if any.
     */
    getComponentForType(type, topology = DEFAULT_TOPOLOGY) {
        return this.getComponentForProperty(type, RENDER_CLASS_NAME, topology);
    }
    /**
     * Efficiently queues a resource to be fetched later.
     *
     * This skips the overhead of creating a promise and allows the subsystem to retrieve multiple resource in one
     * round trip, sacrificing loading status for performance.
     * @renderlibrary This should only be used by render-libraries, not by application code.
     */
    queueEntity(iri, opts) {
        if (!(opts && opts.reload) && !this.shouldLoadResource(iri)) {
            return;
        }
        this.store.getInternalStore().store.transition(iri.value, RecordState.Queued);
        this.resourceQueue.push([iri, opts]);
        this.scheduleResourceQueue();
    }
    /**
     * Queue a linked-delta to be processed.
     *
     * Note: This should only be used by render-libraries (e.g. link-redux), not by application code.
     * @renderlibrary This should only be used by render-libraries, not by application code.
     */
    queueDelta(delta, expedite = false) {
        const quadArr = isQuad(delta[0])
            ? delta.map((s) => rdfFactory.qdrFromQuad(s))
            : delta;
        for (const dp of this.deltaProcessors) {
            dp.queueDelta(quadArr);
        }
        return this.broadcastWithExpedite(expedite);
    }
    /**
     * Will fetch the entity with the URL {iri}. When a resource under that subject is already present, it will not
     * be fetched again.
     *
     * @deprecated Use {queueEntity} instead
     * @param iri The Node of the resource
     * @param opts The options for fetch-/processing the resource.
     * @return A promise with the resulting entity
     */
    async getEntity(iri, opts) {
        const apiOpts = {};
        let preExistingData;
        if (opts && opts.reload) {
            apiOpts.clearPreviousData = true;
            preExistingData = this.tryEntity(iri);
        }
        if (preExistingData !== undefined) {
            // TODO: refactor to use removeRecord
            this.store.removeQuads(preExistingData);
        }
        await this.api.getEntity(iri, apiOpts);
        return this.broadcast();
    }
    /**
     * Resolves all the properties {property} of resource {subject} to their statements.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param {Node} subject The resource to get the properties for.
     * @param {Node | Node[]} property
     * @return {Statement[]} All the statements of {property} on {subject}, or an empty array when none are present.
     */
    getResourcePropertyRaw(subject, property) {
        if (typeof subject === "undefined" || typeof property === "undefined") {
            return [];
        }
        return this.store.getResourcePropertyRaw(subject, property);
    }
    /**
     * Resolves all the properties {property} of resource {subject} to a value.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @typeParam TT The expected return type for the properties.
     * @param {Node} subject The resource to get the properties for.
     * @param {Node | Node[]} property
     * @return {Term[]} The resolved values of {property}, or an empty array when none are present.
     */
    getResourceProperties(subject, property) {
        if (typeof subject === "undefined" || typeof property === "undefined") {
            return [];
        }
        return this.store.getResourceProperties(subject, property);
    }
    /**
     * Resolves the property {property} of resource {subject} to a value.
     *
     * When more than one statement on {property} is present, a random one will be chosen. See
     * {LinkedResourceContainer#getResourceProperties} to retrieve all the values.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @typeParam TT The expected return type for the property.
     * @param {Node} subject The resource to get the properties for.
     * @param {Node | Node[]} property
     * @return {Term | undefined} The resolved value of {property}, or undefined when none are present.
     */
    getResourceProperty(subject, property) {
        if (typeof subject === "undefined" || typeof property === "undefined") {
            return undefined;
        }
        return this.store.getResourceProperty(subject, property);
    }
    getState(recordId) {
        return this.store.getInternalStore().store.getStatus(recordId);
    }
    /**
     * Retrieve the (network) status of the resource {iri}.
     *
     * Status 202 indicates that the resource has been queued for fetching (subject to change).
     * @deprecated
     */
    getStatus(iri) {
        if (iri.termType === TermType.BlankNode) {
            return emptyRequest;
        }
        if (this.resourceQueue.find(([resource]) => equals(resource, iri))) {
            return {
                lastRequested: new Date(),
                lastResponseHeaders: null,
                requested: true,
                status: 202,
                subject: iri,
                timesRequested: 1,
            };
        }
        return this.api.getStatus(iri);
    }
    /**
     * Process a linked-delta onto the store.
     *
     * This should generally only be called from the middleware or the data api
     * @param delta An array of [s, p, o, g] arrays containing the delta.
     * @param expedite Will immediately process the delta rather than waiting for an idle moment, useful in conjunction
     *  with event handlers within the UI needing immediate feedback. Might cause jumpy interfaces.
     */
    processDelta(delta, expedite = false) {
        const processors = this.deltaProcessors;
        for (let i = 0; i < processors.length; i++) {
            processors[i].processDelta(delta);
        }
        return this.broadcastWithExpedite(expedite);
    }
    /**
     * Bulk register components formatted with {LinkedRenderStore.registerRenderer}.
     * @see LinkedRenderStore.registerRenderer
     */
    registerAll(...components) {
        const registerItem = (i) => {
            this.mapping.registerRenderer(i.component, i.type, i.property, i.topology);
        };
        for (let i = 0; i < components.length; i++) {
            const innerRegs = normalizeType(components[i]);
            for (let j = 0; j < innerRegs.length; j++) {
                registerItem(innerRegs[j]);
            }
        }
    }
    /**
     * Remove a resource from the store, when views are still rendered the resource will be re-fetched.
     *
     * @unstable
     */
    removeResource(subject, expedite = false) {
        this.api.invalidate(subject);
        this.store.removeResource(subject);
        return this.broadcastWithExpedite(expedite);
    }
    /**
     * Resets the render store mappings and the schema graph.
     *
     * Note: This should only be used by render-libraries (e.g. link-redux), not by application code.
     */
    reset() {
        this.store = new RDFStore();
        this.schema = new Schema(this.store);
        this.mapping = new ComponentStore(this.schema);
    }
    /**
     * Get a render component for a rendering {property} on resource {subject}.
     *
     * @renderlibrary
     * @param {Node} subject
     * @param {NamedNode | NamedNode[]} predicate
     * @param {NamedNode} topology
     * @return {T | undefined}
     */
    resourcePropertyComponent(subject, predicate, topology) {
        return this.getComponentForProperty(this.store.getResourceProperties(subject, type), predicate, topology || DEFAULT_TOPOLOGY);
    }
    /**
     * Get a render component for {subject}.
     *
     * @renderlibrary
     * @param {Node} subject The resource to get the renderer for.
     * @param {NamedNode} topology The topology to take into account when picking the renderer.
     * @return {T | undefined}
     */
    resourceComponent(subject, topology) {
        return this.getComponentForProperty(this.store.getResourceProperties(subject, type), RENDER_CLASS_NAME, topology || DEFAULT_TOPOLOGY);
    }
    /**
     * Determine if it makes sense to load a resource.
     *
     * @renderlibrary
     * @unstable
     */
    shouldLoadResource(subject) {
        const currentState = this.getState(subject.value).current;
        return subject.termType === "NamedNode" && currentState === RecordState.Absent;
    }
    /**
     * Listen for data changes by subscribing to store changes.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param registration
     * @param registration[0] Will be called with the new statements as its argument.
     * @param registration[1] Options for the callback.
     * @return function Unsubscription function.
     */
    subscribe(registration) {
        registration.subscribedAt = Date.now();
        const subjectFilter = registration.subjectFilter
            ?.flatMap((s) => [s, this.store.getInternalStore().store.primary(s)]);
        if (typeof subjectFilter !== "undefined") {
            for (let i = 0, len = subjectFilter.length; i < len; i++) {
                if (!this.subjectSubscriptions[subjectFilter[i]]) {
                    this.subjectSubscriptions[subjectFilter[i]] = [];
                }
                this.subjectSubscriptions[subjectFilter[i]].push(registration);
            }
            return () => {
                registration.markedForDelete = true;
                this.markForCleanup();
            };
        }
        this.bulkSubscriptions.push(registration);
        return () => {
            registration.markedForDelete = true;
            this.bulkSubscriptions.splice(this.bulkSubscriptions.indexOf(registration), 1);
        };
    }
    /** @internal */
    touch(_iri, _err) {
        this.broadcast();
        return true;
    }
    /**
     * Returns an entity from the cache directly.
     * This won't cause any network requests even if the entity can't be found.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param iri The Node of the resource.
     * @returns The object if found, or undefined.
     */
    tryEntity(iri) {
        return this.store.quadsFor(iri);
    }
    /** @deprecated Use getRecord */
    tryRecord(id) {
        return this.getRecord(id);
    }
    /**
     * Returns a record from the store.
     * This won't cause any network requests even if the record isn't present.
     *
     * @param id The id of the resource.
     * @returns The object if found, or undefined.
     */
    getRecord(id) {
        const recordId = typeof id === "string" ? id : id.value;
        return this.store.getInternalStore().store.getRecord(recordId);
    }
    /**
     * Returns a record from the store, inlining all local ids.
     * This won't cause any network requests even if the record isn't present.
     *
     * @param id The id of the resource.
     * @returns The object if found, or undefined.
     */
    collectRecord(id) {
        const recordId = typeof id === "string" ? id : id.value;
        return this.store.getInternalStore().store.collectRecord(recordId);
    }
    /**
     * Broadcasts buffered to all subscribers.
     * The actual broadcast might be executed asynchronously to prevent lag.
     *
     * @param buffer Controls whether processing can be delayed until enough is available.
     * @param maxTimeout Set to 0 to execute immediately.
     * Note: This should only be used by render-libraries (e.g. link-redux), not by application code.
     */
    broadcast(buffer = true, maxTimeout = 1000) {
        if (maxTimeout !== 0 && this.currentBroadcast || this.broadcastHandle) {
            return this.currentBroadcast || Promise.resolve();
        }
        if (buffer) {
            if (this.store.workAvailable() >= 2) {
                if (this.broadcastHandle) {
                    window.clearTimeout(this.broadcastHandle);
                }
                if ((this.lastPostponed === undefined) || Date.now() - this.lastPostponed <= maxTimeout) {
                    if (this.lastPostponed === undefined) {
                        this.lastPostponed = Date.now();
                    }
                    this.broadcastHandle = window.setTimeout(() => {
                        this.broadcastHandle = undefined;
                        this.broadcast(buffer, maxTimeout);
                    }, 200);
                    return this.currentBroadcast || Promise.resolve();
                }
            }
            this.lastPostponed = undefined;
            this.broadcastHandle = undefined;
        }
        if (this.store.workAvailable() === 0) {
            return Promise.resolve();
        }
        let flushResult = new Set();
        for (const dp of this.deltaProcessors) {
            flushResult = new Set([...flushResult, ...Array.from(dp.flush())]);
        }
        const subjects = Array.from(flushResult);
        const subjectRegs = subjects
            .flatMap((sId) => this.subjectSubscriptions[sId])
            .filter((reg) => reg
            && !reg.markedForDelete
            && (reg.subjectFilter
                ? reg.subjectFilter.some((s) => subjects.includes(s))
                : true));
        if (this.bulkSubscriptions.length === 0 && subjectRegs.length === 0) {
            return Promise.resolve();
        }
        return this.currentBroadcast = new ProcessBroadcast({
            bulkSubscriptions: this.bulkSubscriptions.slice(),
            changedSubjects: subjects,
            subjectSubscriptions: subjectRegs,
            timeout: maxTimeout,
        }).run()
            .then(() => {
            this.currentBroadcast = undefined;
            if (this.store.workAvailable() > 0) {
                this.broadcast();
            }
        });
    }
    broadcastWithExpedite(expedite) {
        return this.broadcast(!expedite, expedite ? 0 : 500);
    }
    markForCleanup() {
        if (this.cleanupTimer) {
            return;
        }
        this.cleanupTimer = window.setTimeout(() => {
            this.cleanupTimer = undefined;
            for (const [k, v] of Object.entries(this.subjectSubscriptions)) {
                this.subjectSubscriptions[k] = v.filter((p) => !p.markedForDelete);
            }
        }, this.cleanupTimout);
    }
    scheduleResourceQueue() {
        if (this.resourceQueueHandle) {
            return;
        }
        if (typeof window === "undefined") {
            setTimeout(this.processResourceQueue, this.resourceQueueFlushTimer);
        }
        else if (typeof window.requestIdleCallback !== "undefined") {
            this.resourceQueueHandle = window.requestIdleCallback(this.processResourceQueue, { timeout: this.resourceQueueFlushTimer });
        }
        else {
            this.resourceQueueHandle = window.setTimeout(this.processResourceQueue, this.resourceQueueFlushTimer);
        }
    }
    async processResourceQueue() {
        this.resourceQueueHandle = undefined;
        const queue = this.resourceQueue;
        this.resourceQueue = [];
        if (this.bulkFetch) {
            await this.api.getEntities(queue);
            return this.broadcast();
        }
        else {
            for (let i = 0; i < queue.length; i++) {
                try {
                    const [iri, opts] = queue[i];
                    await this.getEntity(iri, opts);
                }
                catch (e) {
                    this.report(e);
                }
            }
        }
    }
}

/**
 * Binds various uris to link actions.
 *
 * @see {createStore}
 * @param catchActions {boolean} Set to true to catch all left-over actions to {LinkedRenderStore#execActionByIRI}.
 */
const linkMiddleware = (catchActions = true) => (lrs) => (next) => (action, args) => {
    if (action.value.startsWith(ll$1.ns("data/rdflib/").value)) {
        return Promise.resolve(lrs.touch(args[0], args[1]));
    }
    if (catchActions) {
        return lrs.execActionByIRI(action, args);
    }
    return next(action, args);
};

function applyMiddleware(lrs, ...layers) {
    const storeBound = layers.map((middleware) => middleware(lrs));
    const finish = (a, _o) => Promise.resolve(a);
    return storeBound.reduceRight((composed, f) => {
        const next = f(composed);
        if (!next) {
            throw new Error("Provided middleware did not return handler.");
        }
        return next;
    }, finish);
}
/**
 * Initializes a {LinkedRenderStore} with tied together middleware.
 * @param storeOpts Constructor arguments for the LRS.
 * @param middleware Main middleware, to be executed before the {linkMiddleware}.
 * @param trailingMiddleware Middleware to be placed after the {linkMiddleware}. Note: defining trailing middleware
 *  causes actions not to be executed via {LinkedRenderStore#execActionByIRI} anymore, this behaviour can be enabled
 *  manually in one of the defined middlewares if still desired.
 */
function createStore(storeOpts = {}, middleware = [], trailingMiddleware = []) {
    const LRS = new LinkedRenderStore(storeOpts);
    LRS.dispatch = applyMiddleware(LRS, ...middleware, linkMiddleware(trailingMiddleware.length === 0), ...trailingMiddleware);
    return LRS;
}

class ComponentStoreTestProxy extends ComponentStore {
    publicLookup(predicate, obj, topology) {
        return this.lookup(predicate, obj, topology);
    }
}
const getBasicStore = (opts = {}) => {
    const report = (e) => { throw e; };
    const store = opts.store ?? new RDFStore();
    const processor = opts.processor ?? new DataProcessor({ report, store, ...opts.apiOpts });
    const api = opts.api ?? processor;
    const schema = opts.schema ?? new Schema(store);
    const mapping = opts.mapping ?? new ComponentStoreTestProxy(schema);
    const conf = {
        api,
        mapping,
        report,
        schema,
        store,
    };
    const lrs = createStore(conf);
    lrs.resourceQueueFlushTimer = 0;
    return {
        dispatch: lrs.dispatch,
        forceBroadcast: () => lrs.broadcast(false, 0),
        lrs,
        mapping,
        processor,
        schema,
        store,
    };
};

const hextupleProcessor = {
    acceptValue: 1.0,
    mediaTypes: ["application/hex+x-ndjson"],
    transformer: (store) => (res) => {
        const isExpedited = res.hasOwnProperty("expedite")
            ? res.expedite
            : false;
        return hextuplesTransformer(res)
            .then((delta) => store.queueDelta(delta, isExpedited))
            .then(() => []);
    },
};

/**
 * Processes linked-delta responses.
 */
function linkedDeltaProcessor(lrs) {
    return async function processLinkedDelta(response) {
        let data;
        if (response instanceof Response) {
            data = response.bodyUsed ? "" : await response.text();
        }
        else if (typeof XMLHttpRequest !== "undefined" && response instanceof XMLHttpRequest) {
            data = response.responseText;
        }
        else {
            data = response.body;
        }
        if (!data || data.length === 0) {
            return [];
        }
        const parser = new NQuadsParser(lrs.store.getInternalStore());
        const quads = parser.parseString(data);
        const expedite = response.hasOwnProperty("expedite") ? response.expedite : false;
        lrs.queueDelta(quads, expedite);
        // TODO: Resolve the statements in this request
        return [];
    };
}

const isRdfLibResponse = (res) => typeof res.req !== "undefined" && typeof res.req.termType !== "undefined";
/**
 * Processes a range of media types with parsers from the
 * [rdflib.js package](https://www.npmjs.com/package/rdflib).
 */
const createProcessRDF = (rdfParse) => {
    return async function processRDF(response) {
        let data;
        if (isRdfLibResponse(response)) {
            data = response.responseText;
        }
        else if (response instanceof Response) {
            data = response.bodyUsed ? "" : await response.text();
        }
        else if (response instanceof XMLHttpRequest) {
            data = response.responseText;
        }
        else {
            data = response.body;
        }
        const format = getContentType(response);
        const g = new RDFAdapter();
        await new Promise((resolve) => {
            rdfParse(data, g, getURL(response), format, () => {
                resolve();
            });
        });
        return g.quads;
    };
};

const transformers = {
    createProcessRDF,
    hextupleProcessor,
    linkedDeltaProcessor,
};

/* tslint:disable object-literal-sort-keys */
const setRecord = (id, fields) => ({
    type: "SetRecord",
    id,
    fields,
});
const addField = (id, field, value) => ({
    type: "AddField",
    id,
    field,
    value,
});
const setField = (id, field, value) => ({
    type: "SetField",
    id,
    field,
    value,
});
const deleteAllFieldsMatching = (field, value) => ({
    type: "DeleteAllFieldsMatching",
    field,
    value,
});
const deleteFieldMatching = (id, field, value) => ({
    type: "DeleteFieldMatching",
    id,
    field,
    value,
});
const deleteField = (id, field) => ({
    type: "DeleteField",
    id,
    field,
});
const invalidateRecord = (id) => ({
    type: "InvalidateRecord",
    id,
});
const invalidateAllWithProperty = (field, value) => ({
    type: "InvalidateAllWithProperty",
    field,
    value,
});
/* tslint:enable object-literal-sort-keys */

const createMessageProcessor = (lrs) => {
    const store = lrs.store.getInternalStore().store;
    return (message) => {
        switch (message.type) {
            case "SetRecord": {
                store.setRecord(message.id, message.fields);
                break;
            }
            case "AddField":
            case "SetField": {
                const isPartial = isGlobalId(message.id)
                    && message.field !== type.value
                    && !store.getField(message.id, type.value);
                if (isPartial) {
                    lrs.queueEntity(rdfFactory.namedNode(message.id), { reload: true });
                }
                else if (message.type === "AddField") {
                    store.addField(message.id, message.field, message.value);
                }
                else if (message.type === "SetField") {
                    store.setField(message.id, message.field, message.value);
                }
                break;
            }
            case "DeleteField": {
                store.deleteField(message.id, message.field);
                break;
            }
            case "DeleteFieldMatching": {
                store.deleteFieldMatching(message.id, message.field, message.value);
                break;
            }
            case "DeleteAllFieldsMatching": {
                const matches = lrs.store.match(null, rdfFactory.namedNode(message.field), message.value);
                for (const match of matches) {
                    store.deleteFieldMatching(match[0].value, message.field, message.value);
                }
                break;
            }
            case "InvalidateRecord": {
                if (isGlobalId(message.id)) {
                    lrs.queueEntity(rdfFactory.namedNode(message.id), { reload: true });
                }
                break;
            }
            case "InvalidateAllWithProperty": {
                const matches = lrs.store.match(null, rdfFactory.namedNode(message.field), message.value);
                for (const match of matches) {
                    const id = match[QuadPosition.subject];
                    if (isGlobalId(id.value)) {
                        lrs.queueEntity(id, { reload: true });
                    }
                }
                break;
            }
            default: {
                const error = new Error(`Unknown message: ${JSON.stringify(message)}`);
                lrs.report(error);
                throw error;
            }
        }
    };
};

export default LinkedRenderStore;
export { AttributeKey, ComponentStoreTestProxy, DEFAULT_TOPOLOGY, DataProcessor, LinkedRenderStore, ProcessorError, RDFAdapter, RDFStore, RENDER_CLASS_NAME, RecordState, RequestInitGenerator, Schema, StructuredStore, TypedRecord, addField, allRDFPropertyStatements, allRDFValues, anyRDFValue, createEqualComparator, createMessageProcessor, createStore, deleteAllFieldsMatching, deleteField, deleteFieldMatching, deltaProcessor, equals, getBasicStore, getPropBestLangRaw, getTermBestLang, id, idField, idToValue, invalidateAllWithProperty, invalidateRecord, isDifferentOrigin, isGlobalId, isLocalId, linkMiddleware, list, mergeTerms, normalizeType, seq, setField, setRecord, toGraph, transformers, value };
//# sourceMappingURL=index.js.map
